import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Popover,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Close from "@mui/icons-material/Close";
import { isOigEntitlementError, oiggService } from "@services/OIGGService";
import type {
  AdverseCheckData,
  AdverseCheckExclusion,
  OigEntitlementSnapshot,
  WorkflowMappingField,
  WorkflowMappingData,
} from "@services/OIGGService";
import OigCheckQuotaBar from "./OigCheckQuotaBar";
import OigCheckBlockPicker from "./OigCheckBlockPicker";
import {
  affablebpmIntegrationService,
  ExternalGetRecordAndFilesDetailsResponse,
} from "@services/affablebpmIntegration";
import AffableConnectedIcon from "./AffableConnectedIcon";
import BulkProcessingView from "./BulkProcessingView";
import * as XLSX from "xlsx";
import { useAffableConnect } from "@hooks/useAffableConnect";
import {
  getFacilityHierarchyId,
  getSelectedFacilityFromSession,
} from "@utils/facilityUtils";
import { formatExcelExtractedName } from "@utils/formatUtils";
import {
  ADVERSE_CHECK_NPI_HELPER,
  isAdverseCheckEntryValid,
  isValidOptionalAdverseCheckNpi,
  isRosterRowReady,
} from "@utils/adverseCheckEntryValidation";
import {
  buildRowsForResult as buildOigExportRows,
  exportRowsToAoa,
} from "@utils/oigAdverseExport";
import { auditLogService } from "@services/auditLog";
import { getCurrentUser, getCurrentUserId } from "@utils/userUtils";
import {
  EXCLUSION_RECORD_KEY_UNI_FIELD_NAME,
  exclusionRecordIdentityHash,
} from "@utils/exclusionRecordIdentity";
import {
  EXCLUSION_SOURCES_CHECKED,
  EXCLUSION_SOURCE_FRESHNESS_ALL,
  EXCLUSION_SOURCE_FRESHNESS_PREVIEW,
  buildClearResultSubtitle,
  formatCheckedAtLine,
  formatOperatorName,
} from "@utils/exclusionClearResult";
import { markUserActivity } from "@utils/sessionActivity";
import { toast } from "react-toastify";
import { resolveOigChecksAddonId } from "@services/companyAddons";
import { buildBillingAddonPurchaseSuccessUrl } from "@utils/billingAddonUrls";
import ExclusionCheckShell, {
  type ExclusionCheckSurfaceTab,
} from "./ExclusionCheckShell";
import ExclusionCheckHistoryList from "./ExclusionCheckHistoryList";
import { BULK_ADVERSE_CHECK_POLL_INTERVAL_MS } from "./OIGAdverseCheckForm.bulkPoll";
import { BULK_ADVERSE_CHECK_BUTTON_LABEL } from "./OIGAdverseCheckForm.labels";

/** Excel (and SheetJS) allow at most 31 characters; \ / ? * [ ] : are invalid in names. */
const EXCEL_SHEET_NAME_MAX_LEN = 31;

function excelWorksheetName(raw: string): string {
  const stripped = raw.replace(/[\\/?*[\]:]/g, "").trim() || "Sheet";
  return stripped.slice(0, EXCEL_SHEET_NAME_MAX_LEN);
}

/** Normalize to exactly 3 uppercase letters for timezone abbrev. */
function normalizeTzAbbrev(raw: string | undefined | null): string {
  const letters = String(raw || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  if (!letters) return "UTC";
  return (letters + "XXX").slice(0, 3);
}

/** Browser short timezone name → 3-letter abbrev (e.g. PST, PDT, EST). */
function getLocalTzAbbrev(date: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value;
    return normalizeTzAbbrev(tzPart);
  } catch {
    return "UTC";
  }
}

/** Format YYYY-MM-DD-HH-MM-TZ from a Date in local wall time. */
function formatAdverseStamp(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-");
  const time = [pad(date.getHours()), pad(date.getMinutes())].join("-");
  return `${stamp}-${time}-${getLocalTzAbbrev(date)}`;
}

function sheetUsernameForExport(): string {
  const user = getCurrentUser();
  const raw =
    (typeof user?.username === "string" && user.username) ||
    (typeof user?.email === "string" && user.email) ||
    "user";
  return String(raw).replace(/[\\/?*[\]:\s]/g, "_");
}

function adverseSheetTitle(date: Date = new Date()): string {
  return excelWorksheetName(
    `${formatAdverseStamp(date)}_${sheetUsernameForExport()}`,
  );
}

function adverseFileName(date: Date = new Date()): string {
  return `Adverse_Check_${formatAdverseStamp(date)}.xlsx`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type SubjectType = "Vendor" | "Employee";

type SkippedRosterRow = {
  rowNumber: number;
  name: string;
  reason: string;
  checkType?: SubjectType;
};

function formatExclusionDisplayName(exclusion: AdverseCheckExclusion): string {
  const last = String(exclusion?.name?.last || "").trim();
  const first = String(exclusion?.name?.first || "").trim();
  const middle = String(exclusion?.name?.middle || "").trim();
  if (last && first) {
    return `${last.toUpperCase()}, ${first.toUpperCase()}${middle ? ` ${middle.charAt(0).toUpperCase()}` : ""}`;
  }
  const full = String(exclusion?.name?.fullName || "").trim();
  if (full) return full.toUpperCase();
  return "Name not reported";
}

function composeExclusionAddress(exclusion: AdverseCheckExclusion): string {
  const parts = [
    exclusion?.address?.address,
    exclusion?.address?.city,
    exclusion?.address?.state,
    exclusion?.address?.zipCode,
  ]
    .map((p) => String(p || "").trim())
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

/**
 * Fields that must match to merge cards.
 * Profession and Source updated may differ — those are collected into the card.
 */
function exclusionMergeKey(exclusion: AdverseCheckExclusion): string {
  return [
    exclusionListLabel(exclusion),
    String(exclusion.licenseNumber || "").trim(),
    composeExclusionAddress(exclusion).toUpperCase(),
    String(exclusion.excludedDate || "").trim(),
    String(exclusion.reinstatementDate || "").trim(),
  ].join("|");
}

function preferExclusionDisplayName(
  current: string,
  candidate: AdverseCheckExclusion,
): string {
  const next = formatExclusionDisplayName(candidate);
  if (!current || current === "Name not reported") return next;
  if (next === "Name not reported") return current;
  return current;
}

type MergedExclusionCard = {
  exclusion: AdverseCheckExclusion;
  displayName: string;
  professions: string[];
  sourceUpdatedLines: string[];
};

/** Collapse exclusions that share source/licenses/address/dates; union professions + source updates. */
function mergeExclusionsBySharedFields(
  exclusions: AdverseCheckExclusion[],
): MergedExclusionCard[] {
  const groups = new Map<string, MergedExclusionCard>();
  for (const exclusion of exclusions) {
    const key = exclusionMergeKey(exclusion);
    const freshness = sourceFreshness(exclusion);
    const profession = String(exclusion.profession || "").trim();
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        exclusion,
        displayName: formatExclusionDisplayName(exclusion),
        professions: profession ? [profession] : [],
        sourceUpdatedLines:
          freshness !== "Not provided" ? [freshness] : [],
      });
      continue;
    }
    existing.displayName = preferExclusionDisplayName(
      existing.displayName,
      exclusion,
    );
    if (
      profession &&
      !existing.professions.some(
        (p) => p.toUpperCase() === profession.toUpperCase(),
      )
    ) {
      existing.professions.push(profession);
    }
    if (
      freshness !== "Not provided" &&
      !existing.sourceUpdatedLines.includes(freshness)
    ) {
      existing.sourceUpdatedLines.push(freshness);
    }
  }
  return Array.from(groups.values()).map((card) => ({
    ...card,
    // ISO timestamps sort lexicographically; newest first.
    sourceUpdatedLines: [...card.sourceUpdatedLines].sort((a, b) =>
      b.localeCompare(a),
    ),
  }));
}

/**
 * Normalize spreadsheet Type cell to Vendor vs Employee.
 * Missing / unrecognized / legacy "Provider" → Employee.
 */
function parseBulkRowCheckType(cell: unknown): BulkAdverseCheckType {
  const s = String(cell ?? "").trim().toLowerCase();
  if (!s) return "Employee";
  if (s.startsWith("vendor")) return "Vendor";
  return "Employee";
}

/** AffableFLOW workflows remain Vendor/Provider; map roster Type for that API. */
function toAffableWorkflowType(
  checkType: BulkAdverseCheckType | undefined,
): "Vendor" | "Provider" {
  return checkType === "Vendor" ? "Vendor" : "Provider";
}

/** Excel-style column preview in the bulk upload dialog (6 columns including Type). */
function BulkAdverseUploadExcelPreview() {
  const border = "1px solid #c6c6c6";
  const cellBase = {
    borderRight: border,
    borderBottom: border,
    px: 1.5,
    py: 1,
    fontFamily: '"Segoe UI", "Rethink Sans", Roboto, sans-serif',
    fontSize: "14px",
    lineHeight: 1.35,
    color: "#1f2937",
    backgroundColor: "#fff",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
  } as const;

  const cols = [
    { letter: "A", header: "Name (Required)", sample: "Jonathan Henke" },
    { letter: "B", header: "NPI (Optional)", sample: "1326005646" },
    { letter: "C", header: "SSN (Optional)", sample: "—" },
    { letter: "D", header: "Phone (Optional)", sample: "—" },
    { letter: "E", header: "License (Optional)", sample: "—" },
    {
      letter: "F",
      header: "Type (Optional)",
      sample: "Employee",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #000",
        borderRadius: 1,
        overflow: "hidden",
        boxSizing: "border-box",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1.15fr) repeat(4, minmax(0,0.85fr)) minmax(0,1.05fr)",
          flex: 1,
          minHeight: 0,
        }}
      >
        {cols.map((c) => (
          <Box
            key={`L-${c.letter}`}
            sx={{
              ...cellBase,
              justifyContent: "center",
              backgroundColor: "#f0f0f0",
              fontWeight: 600,
              color: "#404040",
            }}
          >
            {c.letter}
          </Box>
        ))}
        {cols.map((c) => (
          <Box
            key={`H-${c.letter}`}
            sx={{
              ...cellBase,
              backgroundColor: "#e4e4e4",
              fontWeight: 700,
              fontSize: "13px",
            }}
          >
            {c.header}
          </Box>
        ))}
        {cols.map((c) => (
          <Box
            key={`S-${c.letter}`}
            sx={{
              ...cellBase,
              color: c.sample === "—" ? "#9ca3af" : "#374151",
              fontStyle: c.sample === "—" ? "italic" : "normal",
            }}
          >
            {c.sample}
          </Box>
        ))}
      </Box>
      <Typography
        sx={{
          fontSize: "13px",
          color: "#737373",
          px: 1.5,
          py: 0.75,
          borderTop: border,
          backgroundColor: "#fafafa",
        }}
      >
        Use &quot;Vendor&quot; or &quot;Employee&quot; in column F; omit column F to default all
        rows to Employee.
      </Typography>
    </Box>
  );
}

// Match RiskAnalysis accordion styling
const StyledAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  overflow: "hidden",
  "&:before": {
    display: "none",
  },
  "& .accordion-title": {
    color: "#262626",
    transition: "color 0.3s ease",
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
  "& .MuiAccordionSummary-root": {
    borderRadius: "8px 8px 0 0",
  },
  "&.Mui-expanded .MuiAccordionSummary-root": {
    borderRadius: "8px 8px 0 0",
  },
  "& .MuiAccordionDetails-root": {
    borderRadius: "0 0 8px 8px",
  },
});

const Row = styled(Box)({
  display: "flex",
  gap: "24px",
  width: "100%",
  alignItems: "flex-start",
  flexWrap: "wrap",
});

const CompactFieldGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  width: "100%",
  minWidth: 0,
});

const CompactField = styled(TextField)({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fdffff",
    "& fieldset": {
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
    },
    "&:hover fieldset": {
      border: "1px solid #0a829d",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #0a829d",
    },
    "& input, & .MuiSelect-select": {
      padding: "9px 12px",
      fontFamily: "Rethink Sans, sans-serif",
      fontWeight: 500,
      fontSize: "13px",
      color: "#262626",
    },
  },
});

const CompactFieldLabel = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  color: "#334155",
});

type BulkAdverseCheckType = "Vendor" | "Employee";

interface ParsedUser {
  id: number;
  name?: string;
  npi?: string;
  /** Optional SSN is used only for the in-flight adverse-check request. */
  ssn?: string;
  phoneNumber?: string;
  licenses?: Array<{ number: string }>;
  /** From spreadsheet Type column; defaults to Employee when column absent. */
  checkType?: BulkAdverseCheckType;
  /** AffableFLOW document id when the row came from Upload from AffableFLOW (maps to backend InputRoot.DocumentID). */
  affableDocumentId?: number;
}

interface AnalysisResult {
  success: boolean;
  data?: AdverseCheckData;
  error?: { message: string };
  user: ParsedUser;
}

interface AffableSelectableRecord {
  key: string;
  raw: Record<string, unknown>;
  user: ParsedUser;
}

type OIGFormProps = {
  onSuccess?: () => void;
  facilityData?: any;
  /** True when the OIG / Exclusion tab is visible. */
  oigTabActive?: boolean;
  onWatchGuideClick?: () => void;
  watchGuideLoading?: boolean;
  watchGuideIconSrc?: string;
  showWatchGuide?: boolean;
  /** Called after a history batch is saved so the History tab can refetch (AB#10338). */
  onHistoryPersisted?: () => void;
};

type AffableWorkflowType = "Vendor" | "Provider";

function ProviderOIGAdverseCheckForm({
  onSuccess,
  facilityData,
  oigTabActive = false,
  onWatchGuideClick,
  watchGuideLoading = false,
  watchGuideIconSrc,
  showWatchGuide = true,
  onHistoryPersisted,
}: OIGFormProps) {
  const [subjectType, setSubjectType] = useState<SubjectType>("Employee");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [entityName, setEntityName] = useState("");
  const [npi, setNpi] = useState("");
  const [ssn, setSsn] = useState("");
  const [phone, setPhone] = useState("");
  const [license, setLicense] = useState("");
  const [dob, setDob] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [addressState, setAddressState] = useState("");
  /** Vendor type uses entity-name search (same API path as former Entity checkbox). */
  const isEntitySearch = subjectType === "Vendor";

  /** Compose API `name` from OIG-style individual or entity fields. */
  const searchName = useMemo(() => {
    if (isEntitySearch) {
      return entityName.trim();
    }
    const first = firstName.trim();
    const last = lastName.trim();
    if (!last) {
      return "";
    }
    if (first) {
      return `${first} ${last}`;
    }
    return last;
  }, [isEntitySearch, entityName, firstName, lastName]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [result, setResult] = useState<AdverseCheckData | null>(null);
  /** Timestamp of the last completed single check (for clear/confirmed result UI). */
  const [singleCheckedAt, setSingleCheckedAt] = useState<Date | null>(null);

  // Bulk check states
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showAffableWorkflowDialog, setShowAffableWorkflowDialog] = useState(false);
  const [selectedAffableWorkflowType, setSelectedAffableWorkflowType] =
    useState<AffableWorkflowType>("Vendor");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedUsers, setUploadedUsers] = useState<ParsedUser[]>([]);
  const [skippedRosterRows, setSkippedRosterRows] = useState<SkippedRosterRow[]>(
    [],
  );
  const [rosterParsedRowCount, setRosterParsedRowCount] = useState(0);
  const [rosterReadyCount, setRosterReadyCount] = useState(0);
  const [rosterRunLabel, setRosterRunLabel] = useState("Roster check");
  const [bulkCompletedAt, setBulkCompletedAt] = useState<Date | null>(null);
  const [analysisResults, setAnalysisResults] = useState<
    (AnalysisResult | null)[]
  >([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bulkCompletedCount, setBulkCompletedCount] = useState(0);
  const [isAddingToAffable, setIsAddingToAffable] = useState(false);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(-1);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [oigEntitlement, setOigEntitlement] =
    useState<OigEntitlementSnapshot | null>(null);
  const [showOigBlockPicker, setShowOigBlockPicker] = useState(false);
  const [isAffableImportMode, setIsAffableImportMode] = useState(false);
  const [isSyncingAffableRecords, setIsSyncingAffableRecords] = useState(false);
  const [affableSyncError, setAffableSyncError] = useState<string | null>(null);
  const [affableSelectableRecords, setAffableSelectableRecords] = useState<
    AffableSelectableRecord[]
  >([]);
  const [selectedAffableRecordKeys, setSelectedAffableRecordKeys] = useState<string[]>(
    [],
  );
  const [affableTablePage, setAffableTablePage] = useState(1);
  const [affableRowsPerPage, setAffableRowsPerPage] = useState(5);
  /** True when displayed bulk results came from Perform Exclusion Check on AffableFLOW-selected rows. */
  const [bulkAdverseResultsFromAffableImport, setBulkAdverseResultsFromAffableImport] =
    useState(false);

  // Info popover for exclusion/sanction descriptions
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const hoverOpenRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const infoPopoverOpen = Boolean(infoAnchorEl);

  const clearHoverOpen = useCallback(() => {
    if (hoverOpenRef.current) {
      clearTimeout(hoverOpenRef.current);
      hoverOpenRef.current = null;
    }
  }, []);

  const clearHoverClose = useCallback(() => {
    if (hoverCloseRef.current) {
      clearTimeout(hoverCloseRef.current);
      hoverCloseRef.current = null;
    }
  }, []);

  const handleInfoClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    clearHoverOpen();
    clearHoverClose();
    setInfoAnchorEl((prev) => (prev ? null : event.currentTarget));
  }, [clearHoverOpen, clearHoverClose]);

  const handleInfoMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      clearHoverClose();
      hoverOpenRef.current = setTimeout(() => {
        hoverOpenRef.current = null;
        setInfoAnchorEl(event.currentTarget);
      }, 350);
    },
    [clearHoverClose],
  );

  const handleInfoMouseLeave = useCallback(() => {
    clearHoverOpen();
  }, [clearHoverOpen]);

  const handleInfoPopoverClose = useCallback(() => {
    setInfoAnchorEl(null);
    clearHoverOpen();
    clearHoverClose();
  }, [clearHoverOpen, clearHoverClose]);

  useEffect(() => {
    return () => {
      clearHoverOpen();
      clearHoverClose();
    };
  }, [clearHoverOpen, clearHoverClose]);

  const oigPageAuditLoggedRef = useRef<string | null>(null);

  // Log page access when the OIG tab is visible
  useEffect(() => {
    if (!oigTabActive) {
      oigPageAuditLoggedRef.current = null;
      return;
    }
    const facility = facilityData || getSelectedFacilityFromSession();
    const facilityId = facility?.facility_id;
    if (!facilityId) return;
    const key = String(facilityId);
    if (oigPageAuditLoggedRef.current === key) return;
    oigPageAuditLoggedRef.current = key;

    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);

      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: "/api/v1/oig/get-adverse-check",
        method: "GET",
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: "Exclusion & Sanction Check",
          action: "VIEW",
        },
        action_type: "VIEW",
        action_status: "SUCCESS",
      });
    };

    void logPageAccess();
  }, [oigTabActive, facilityData]);

  // ------------------- Download helpers (Excel) -------------------
  // Shared with history persist so results download == history Results sheet (#10262).
  const buildRowsForResult = (res: AnalysisResult, user?: ParsedUser) =>
    buildOigExportRows(res as any, user, (data) =>
      oiggService.formatSsnVerificationLabel(data as any),
    );

  const resolveFacilityIds = () => {
    const facility = getFacilityData();
    const rawId = facility
      ? getFacilityHierarchyId(
          facility as Parameters<typeof getFacilityHierarchyId>[0],
        )
      : null;
    const facilityId = Number(rawId ?? 0);
    const orgIdRaw =
      (facility as { org_id?: unknown } | null | undefined)?.org_id;
    const orgId =
      orgIdRaw === null || orgIdRaw === undefined || orgIdRaw === ""
        ? null
        : Number(orgIdRaw);
    return {
      facilityId: Number.isFinite(facilityId) && facilityId > 0 ? facilityId : 0,
      orgId:
        orgId !== null && Number.isFinite(orgId) && orgId > 0 ? orgId : null,
    };
  };

  const refreshOigEntitlement = useCallback(async () => {
    const { facilityId } = resolveFacilityIds();
    if (!facilityId) {
      return;
    }
    try {
      const snapshot = await oiggService.getOigEntitlement(facilityId);
      setOigEntitlement(snapshot);
    } catch {
      /* quota chip is optional; paid paths still enforce on the API */
    }
  }, [facilityData]);

  const oigChecksExhausted =
    oigEntitlement !== null && oigEntitlement.remaining <= 0;

  useEffect(() => {
    void refreshOigEntitlement();
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("oigAddon") === "1") {
        setShowOigBlockPicker(false);
        const { facilityId } = resolveFacilityIds();
        void (async () => {
          try {
            const addonId = await resolveOigChecksAddonId(facilityId);
            const target = addonId
              ? buildBillingAddonPurchaseSuccessUrl(addonId)
              : `${window.location.origin}/dashboard/billing?tab=addons&oigAddon=1`;
            window.location.replace(target);
          } catch {
            await refreshOigEntitlement();
          }
        })();
      }
    }
  }, [refreshOigEntitlement]);

  const persistHistoryBatch = async (args: {
    queriedAt: Date;
    subjects: Array<{
      name: string;
      npi?: string;
      phone?: string;
      licenses?: Array<{ number: string }>;
      ssn?: string;
      response?: Record<string, unknown> | AdverseCheckData | null;
    }>;
    exportRows: Array<Record<string, unknown>>;
  }) => {
    const { facilityId, orgId } = resolveFacilityIds();
    if (!facilityId || args.subjects.length === 0) return;
    try {
      await oiggService.saveAdverseCheckHistory({
        facility_id: facilityId,
        org_id: orgId,
        queried_at: args.queriedAt.toISOString(),
        tz_abbrev: getLocalTzAbbrev(args.queriedAt),
        subjects: args.subjects,
        export_rows: args.exportRows,
      });
      // Invalidate History tab list so the new run appears without re-login (AB#10338).
      onHistoryPersisted?.();
    } catch (err) {
      console.error("Failed to persist adverse check history:", err);
    }
  };

  const handleDownloadCurrent = () => {
    const wb = XLSX.utils.book_new();
    const combined: any[] = [];
    analysisResults.forEach((res, i) => {
      if (!res) return;
      const user = uploadedUsers[i];
      combined.push(...buildRowsForResult(res, user));
    });
    skippedRosterRows.forEach((row) => {
      combined.push({
        "User Name": row.name || "",
        NPI: "",
        SSN: "",
        Phone: "",
        Licenses: "",
        Agency: "",
        "Match Score": "",
        Status: "Skipped",
        "Exclusion Count": 0,
        Details: `Row ${row.rowNumber}: ${row.reason}`,
        Type: row.checkType || "",
      });
    });
    if (combined.length === 0) return;
    const now = new Date();
    // Canonical column order so New Check matches History Results (#10333)
    const ws = XLSX.utils.aoa_to_sheet(exportRowsToAoa(combined));
    XLSX.utils.book_append_sheet(wb, ws, adverseSheetTitle(now));
    XLSX.writeFile(wb, adverseFileName(now));
  };

  // Download currently displayed single-result (non-bulk) as Excel
  const handleDownloadCurrentSingle = (data: AdverseCheckData | null) => {
    if (!data) return;
    const resLike: any = { data };
    // API returns data directly on item, not wrapped in "professional"
    const firstItem = data?.items?.[0];
    const userLike: ParsedUser = {
      id: 0,
      name: firstItem?.names?.[0]?.fullName || searchName || "",
      npi: firstItem?.npi || npi || "",
      ssn: ssn || "",
      phoneNumber: phone || "",
      licenses: license ? [{ number: license }] : [],
    } as any;

    const now = new Date();
    const wb = XLSX.utils.book_new();
    const rows = buildRowsForResult(resLike, userLike);
    const ws = XLSX.utils.aoa_to_sheet(exportRowsToAoa(rows));
    XLSX.utils.book_append_sheet(wb, ws, adverseSheetTitle(now));
    XLSX.writeFile(wb, adverseFileName(now));
  };

  const getFacilityData = useCallback(
    () => facilityData || getSelectedFacilityFromSession(),
    [facilityData],
  );

  const { connectToAffableBPM: handleConnectAffableBPM, isConnectingAffable } =
    useAffableConnect({ getFacilityData, workflowType: "Provider" });

  // Resolve facility once for render-time checks (e.g., Affable connection status)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resolvedFacility = useMemo(() => getFacilityData(), [facilityData]);
  const isAffableConnected = useMemo(() => {
    const value: unknown = (
      resolvedFacility as { is_affable_connected?: unknown } | null | undefined
    )?.is_affable_connected;
    if (value === true || value === 1 || value === "1") return true;
    if (typeof value === "string") return value.trim().toLowerCase() === "true";
    return false;
  }, [resolvedFacility]);

  const npiFieldError = useMemo(() => {
    if (isValidOptionalAdverseCheckNpi(npi)) return "";
    return ADVERSE_CHECK_NPI_HELPER;
  }, [npi]);

  const isValid = useMemo(
    () =>
      isAdverseCheckEntryValid({
        isEntitySearch,
        entityName,
        lastName,
        npi,
      }),
    [isEntitySearch, entityName, lastName, npi],
  );

  const getStringFromRecord = useCallback(
    (
      record: Record<string, unknown>,
      preferredKeys: string[],
      partialKeyHints: string[] = [],
    ): string => {
      for (const key of preferredKeys) {
        const value = record[key];
        if (typeof value === "string" && value.trim()) return value.trim();
        if (typeof value === "number" && Number.isFinite(value)) return String(value);
      }
      const loweredEntries = Object.entries(record).map(([k, v]) => [
        k.toLowerCase(),
        v,
      ] as const);
      for (const hint of partialKeyHints.map((h) => h.toLowerCase())) {
        const found = loweredEntries.find(([k]) => k.includes(hint));
        if (!found) continue;
        const value = found[1];
        if (typeof value === "string" && value.trim()) return value.trim();
        if (typeof value === "number" && Number.isFinite(value)) return String(value);
      }
      return "";
    },
    [],
  );

  const getMappedValueFromRecord = useCallback(
    (
      record: Record<string, unknown>,
      mappingFields: WorkflowMappingField[] | null,
      targetUniFieldNames: string[],
    ): string => {
      if (!mappingFields || mappingFields.length === 0) return "";

      const targetSet = new Set(targetUniFieldNames.map((n) => n.toLowerCase()));
      const mapped = mappingFields.find((field) => {
        const uni = String(field?.Uni_FieldName || "").trim().toLowerCase();
        return targetSet.has(uni);
      });
      if (!mapped) return "";

      const mappedFieldName = String(mapped.FieldName || "").trim();
      if (!mappedFieldName) return "";

      const direct = record[mappedFieldName];
      if (typeof direct === "string" && direct.trim()) return direct.trim();
      if (typeof direct === "number" && Number.isFinite(direct)) return String(direct);

      const caseInsensitiveKey = Object.keys(record).find(
        (k) => k.toLowerCase() === mappedFieldName.toLowerCase(),
      );
      if (!caseInsensitiveKey) return "";
      const value = record[caseInsensitiveKey];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number" && Number.isFinite(value)) return String(value);
      return "";
    },
    [],
  );

  const normalizeAffableRecordToUser = useCallback(
    (
      record: Record<string, unknown>,
      index: number,
      mappingFields: WorkflowMappingField[] | null = null,
    ): AffableSelectableRecord => {
      const fullNameValue =
        getMappedValueFromRecord(record, mappingFields, [
          "Name",
          "Provider/Vendor Name",
        ]) ||
        getStringFromRecord(
        record,
        [
          "Name",
          "FullName",
          "Full Name",
          "ProviderName",
          "Provider Name",
          "VendorName",
          "Vendor Name",
          "ProfessionalName",
        ],
        ["name", "provider", "vendor"],
      );
      const npiValue =
        getMappedValueFromRecord(record, mappingFields, ["NPI", "NPI Number"]) ||
        getStringFromRecord(
          record,
          ["NPI", "NPINumber", "NPI Number", "ProviderNPI"],
          ["npi"],
        );
      const phoneValue =
        getMappedValueFromRecord(record, mappingFields, [
          "Phone Number",
          "Phone",
        ]) ||
        getStringFromRecord(
          record,
          ["Phone", "PhoneNumber", "Phone Number"],
          ["phone"],
        );
      const licenseValue =
        getMappedValueFromRecord(record, mappingFields, [
          "License Number",
          "License",
        ]) ||
        getStringFromRecord(
          record,
          ["License", "LicenseNumber", "License Number"],
          ["license"],
        );
      const typeRaw = getStringFromRecord(
        record,
        ["ProviderVendor", "Provider/Vendor", "Type", "CheckType"],
        ["provider", "vendor", "type"],
      );
      const rowNum = getStringFromRecord(record, ["RowNum"], ["rownum"]);
      const documentIdStr = getStringFromRecord(
        record,
        ["DocumentID", "DocumentId"],
        ["documentid", "document"],
      ).trim();
      let affableDocumentId: number | undefined;
      if (documentIdStr !== "") {
        const n = Number(documentIdStr);
        if (Number.isFinite(n)) {
          affableDocumentId = n;
        }
      }

      return {
        key: `${documentIdStr || "doc"}-${rowNum || index}`,
        raw: record,
        user: {
          id: index + 1,
          name: fullNameValue || `Record ${index + 1}`,
          npi: npiValue,
          phoneNumber: phoneValue,
          licenses: licenseValue ? [{ number: licenseValue }] : [],
          checkType: parseBulkRowCheckType(typeRaw),
          affableDocumentId,
        },
      };
    },
    [getMappedValueFromRecord, getStringFromRecord],
  );

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    setResult(null); // Clear previous result first
    setSingleCheckedAt(null);
    const queriedAt = new Date();
    try {
      const normalizedSsn = ssn.replace(/\D/g, "");
      if (ssn.trim() && normalizedSsn.length !== 9) {
        setError("SSN must contain exactly 9 digits.");
        return;
      }
      const { facilityId } = resolveFacilityIds();
      if (!facilityId) {
        setError("Facility is required for exclusion checks.");
        return;
      }
      const data = await oiggService.performAdverseCheck(
        searchName,
        npi.trim(),
        normalizedSsn,
        phone.trim(),
        [license.trim()],
        facilityId,
        isEntitySearch
          ? undefined
          : {
              firstName: firstName.trim() || undefined,
              lastName: lastName.trim() || undefined,
            },
      );
      void refreshOigEntitlement();
      setResult(data);
      setSingleCheckedAt(queriedAt);
      setSuccessMsg("Exclusion check completed successfully.");
      const userLike: ParsedUser = {
        id: 0,
        name: searchName,
        npi: npi.trim() || undefined,
        ssn: normalizedSsn || undefined,
        phoneNumber: phone.trim() || undefined,
        licenses: license.trim() ? [{ number: license.trim() }] : [],
      };
      const exportRows = buildRowsForResult({ data } as any, userLike);
      void persistHistoryBatch({
        queriedAt,
        subjects: [
          {
            name: searchName,
            npi: npi.trim() || undefined,
            phone: phone.trim() || undefined,
            licenses: license.trim() ? [{ number: license.trim() }] : undefined,
            ssn: normalizedSsn || undefined,
            response: data as unknown as Record<string, unknown>,
          },
        ],
        exportRows,
      });
      if (onSuccess) onSuccess();
    } catch (e: any) {
      if (isOigEntitlementError(e)) {
        setShowOigBlockPicker(true);
        setOigEntitlement((current) =>
          current
            ? { ...current, remaining: e.remaining ?? 0, used: e.used ?? current.used, limit: e.limit ?? current.limit }
            : current,
        );
        setError(e.message);
        return;
      }
      const detailed =
        e?.error?.details?.message ||
        e?.details?.message ||
        e?.message ||
        (typeof e === "string" ? e : null);
      setError(detailed || "Failed to perform exclusion check");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    setSubjectType("Employee");
    setLastName("");
    setFirstName("");
    setEntityName("");
    setNpi("");
    setSsn("");
    setPhone("");
    setLicense("");
    setDob("");
    setAddressLine("");
    setAddressState("");
    setResult(null);
    setSingleCheckedAt(null);
    setError(null);
    setSuccessMsg(null);
  };

  const handleBulkAdverseCheck = () => {
    setShowBulkDialog(true);
  };

  const startAffableImportMode = async (workflowType: AffableWorkflowType) => {
    setIsAffableImportMode(true);
    // Hide/clear manual adverse-check state when switching to Affable import mode.
    setSubjectType("Employee");
    setLastName("");
    setFirstName("");
    setEntityName("");
    setNpi("");
    setSsn("");
    setPhone("");
    setLicense("");
    setDob("");
    setAddressLine("");
    setAddressState("");
    setResult(null);
    setSingleCheckedAt(null);
    setSuccessMsg(null);
    setError(null);
    setUploadedUsers([]);
    setSkippedRosterRows([]);
    setRosterParsedRowCount(0);
    setRosterReadyCount(0);
    setAnalysisResults([]);
    setBulkAdverseResultsFromAffableImport(false);
    setBulkError(null);
    setSelectedAffableWorkflowType(workflowType);
    await fetchAffableRecords(workflowType);
  };

  const handleUploadFromAffableFlow = () => {
    setShowAffableWorkflowDialog(true);
  };

  const handleCloseBulkDialog = () => {
    setShowBulkDialog(false);
    // Only reset file selection and errors when closing dialog
    // Keep uploaded users and results visible on main page
    setSelectedFile(null);
    setSkippedRosterRows([]);
    setRosterParsedRowCount(0);
    setRosterReadyCount(0);
    setBulkError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setBulkError(null);
      setSkippedRosterRows([]);
      setRosterParsedRowCount(0);
      setRosterReadyCount(0);
      void processExcelFile(file, { previewOnly: true }).catch(() => {
        /* bulkError already set */
      });
    }
  };

  const downloadSkippedRosterReport = () => {
    if (skippedRosterRows.length === 0) return;
    const rows = [
      ["Row", "Name", "Reason"],
      ...skippedRosterRows.map((r) => [
        String(r.rowNumber),
        r.name,
        r.reason,
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, excelWorksheetName("Skipped rows"));
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    triggerBlobDownload(
      new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `roster_skipped_rows_${formatAdverseStamp()}.xlsx`,
    );
  };

  const handleAddToAffable = async () => {
    setIsAddingToAffable(true);
    try {
      const orgName = facilityData.org_name || "test";
      console.log("**********Facility Data***********", facilityData);

      const typesNeeded = new Set(
        uploadedUsers.map((u) => u.checkType ?? "Employee"),
      );

      const mappingByType: Partial<
        Record<BulkAdverseCheckType, WorkflowMappingData>
      > = {};

      for (const t of typesNeeded) {
        const affableType = toAffableWorkflowType(t);
        const workflowMappingResponse = await oiggService.getWorkflowMapping(
          affableType,
          orgName,
        );
        console.log(
          `Workflow mapping fetched (${t} → ${affableType}):`,
          workflowMappingResponse,
        );
        if (!workflowMappingResponse || workflowMappingResponse.length === 0) {
          console.error(`No workflow mapping found for ${affableType}`);
          setIsAddingToAffable(false);
          return;
        }
        mappingByType[t] = workflowMappingResponse[0];
      }

      // Each array element matches backend InputRoot: strObjInput, strObjInputTrans, DocumentID (0 when not from Affable).
      const processedData = (
        await Promise.all(
          uploadedUsers.map(async (_, index) => {
            const analysisResult = analysisResults[index];
            if (!analysisResult || !analysisResult.data) {
              return null;
            }

            const rowUser = analysisResult.user;

            const rowType = rowUser.checkType ?? "Employee";
            const workflowMapping = mappingByType[rowType];
            if (!workflowMapping) {
              return null;
            }

            const fieldMappings = workflowMapping.field_Mapping;

            const adverseCheckData = analysisResult.data as any;

            // Calculate status from adverse check result - matching the display logic from lines 1942-2071
            let status = "Not found";
            if (analysisResult.success && analysisResult.data) {
              status =
                adverseCheckData.total_exclusions > 0
                  ? "Exclusion Found"
                  : "Verified";
              const ssnLabel = oiggService.formatSsnVerificationLabel(
                adverseCheckData,
              );
              if (ssnLabel) {
                status = `${status} (${ssnLabel})`;
              }
            }

            const exclusionRecordKey = await exclusionRecordIdentityHash({
              name: rowUser.name || "",
              npi: rowUser.npi,
              phone: rowUser.phoneNumber,
              license: rowUser?.licenses?.[0]?.number,
            });

            // Create grid data object to hold all field values
            const gridData: { [key: string]: any } = {
              Name: rowUser.name || "",
              NPI: rowUser.npi || "",
              "License Number": rowUser?.licenses?.[0]?.number || "",
              "Phone Number": rowUser.phoneNumber || "",
              [EXCLUSION_RECORD_KEY_UNI_FIELD_NAME]: exclusionRecordKey,
              Status: status,
              "Exclusion Count": adverseCheckData.total_exclusions || 0,
              "Exclusion Details": adverseCheckData.summary || "",
            };

            // Create ColList array from field mappings with Col_Parent === 0 (base fields only)
            const colList = fieldMappings
              .filter((field) => field.Col_Parent === 0)
              .map((field) => {
                const colValue = gridData[field.Uni_FieldName] ?? "";

                return {
                  colname: field.FieldName,
                  colvalue: String(colValue),
                  coltype: 0,
                };
              });

            // Create the strObjInput object (base fields with Col_Parent === 0)
            const strObjInput = {
              ColList: colList,
              Deptid: workflowMapping.workflowId,
              Orgid: workflowMapping.orgid,
            };

            // Create ColList for transaction fields (Col_Parent > 0)
            const transColList = fieldMappings
              .filter((field) => field.Col_Parent > 0)
              .map((field) => {
                const colValue = gridData[field.Uni_FieldName] ?? "";

                return {
                  colname: field.FieldName,
                  colvalue: String(colValue),
                  coltype: 0,
                };
              });

            // Create the strObjInputTrans array (child fields with Col_Parent > 0)
            const strObjInputTrans = transColList;

            const documentId =
              rowUser.affableDocumentId != null &&
              Number.isFinite(rowUser.affableDocumentId)
                ? Math.trunc(rowUser.affableDocumentId)
                : 0;

            return {
              strObjInput,
              strObjInputTrans,
              DocumentID: documentId,
            };
          }),
        )
      ).filter((item) => item !== null);

      console.log(
        "Created strObjInput and strObjInputTrans objects:",
        processedData,
      );

      // Send processedData to UniComply API with orgName
      const result = await oiggService.processExclusionData(
        processedData,
        orgName,
      );
      console.log("Process exclusion data result:", result);
      toast.success("Exclusion check status saved in AffableFLOW successfully");
    } catch (error) {
      console.error("Error in Add to Affable:", error);
      const msg =
        error instanceof Error
          ? error.message
          : "Failed to save exclusion status in AffableFLOW.";
      toast.error(msg);
    } finally {
      setIsAddingToAffable(false);
    }
  };

  const processExcelFile = useCallback(
    (
      file: File,
      options?: { previewOnly?: boolean },
    ): Promise<ParsedUser[]> => {
      return new Promise((resolve, reject) => {
        console.log("Processing Excel file:", file.name);
        setBulkError(null);

        // Validate file type
        const validTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
        ];

        const validExtensions = [".xlsx", ".xls"];
        const fileName = file.name.toLowerCase();
        const hasValidExtension = validExtensions.some((ext) =>
          fileName.endsWith(ext),
        );

        if (!validTypes.includes(file.type) && !hasValidExtension) {
          setBulkError("Please upload a valid Excel file (.xlsx or .xls)");
          reject(new Error("Invalid file type"));
          return;
        }

        // Check file size (max 10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          setBulkError("File size must be less than 10MB");
          reject(new Error("File too large"));
          return;
        }

        // Read and parse the Excel file
        const reader = new FileReader();
        reader.onload = function (e) {
          try {
            const data = new Uint8Array(e.target!.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });

            // Get the first worksheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
            }) as any[][];

            if (jsonData.length < 2) {
              setBulkError(
                "Excel file must contain at least a header row and one data row",
              );
              reject(new Error("Insufficient data"));
              return;
            }

            const parsed = parseExcelData(jsonData);
            setRosterParsedRowCount(parsed.totalDataRows);
            setRosterReadyCount(parsed.ready.length);
            setSkippedRosterRows(parsed.skipped);

            if (parsed.ready.length === 0 && parsed.skipped.length === 0) {
              setBulkError("No valid user data found in Excel file");
              reject(new Error("No valid data"));
              return;
            }

            if (!options?.previewOnly) {
              setUploadedUsers(parsed.ready);
              setAnalysisResults(parsed.ready.map(() => null));
              setBulkAdverseResultsFromAffableImport(false);
            }

            console.log("Ready user count:", parsed.ready.length);
            console.log("Skipped user count:", parsed.skipped.length);
            resolve(parsed.ready);
          } catch (error) {
            console.error("Error parsing Excel file:", error);
            setBulkError(
              "Error reading Excel file. Please check the file format.",
            );
            reject(error);
          }
        };

        reader.onerror = () => {
          setBulkError("Error reading file");
          reject(new Error("File read error"));
        };

        reader.readAsArrayBuffer(file);
      });
    },
    [],
  );

  const parseExcelData = (
    jsonData: any[][],
  ): {
    ready: ParsedUser[];
    skipped: SkippedRosterRow[];
    totalDataRows: number;
  } => {
    // Do not log spreadsheet rows because optional SSNs may be present.

    const headers = jsonData[0].map((h) =>
      h ? h.toString().toLowerCase().trim() : "",
    );
    const ready: ParsedUser[] = [];
    const skipped: SkippedRosterRow[] = [];
    let totalDataRows = 0;

    console.log("Headers found:", headers);

    // Find column indices - more flexible matching
    const nameIndex = headers.findIndex(
      (h) => h.includes("name") && !h.includes("first") && !h.includes("last"),
    );
    const firstNameIndex = headers.findIndex((h) => h.includes("first"));
    const lastNameIndex = headers.findIndex((h) => h.includes("last"));
    const npiIndex = headers.findIndex((h) => h.includes("npi"));
    const ssnIndex = headers.findIndex(
      (h) => h.includes("ssn") || h.includes("social security"),
    );
    const phoneIndex = headers.findIndex((h) => h.includes("phone"));
    const licenseIndex = headers.findIndex(
      (h) => h.includes("license") && !h.includes("state"),
    );
    // Prefer "Type"; still accept legacy "Provider/Vendor" header.
    const typeColumnIndex = headers.findIndex(
      (h) =>
        h === "type" ||
        (h.includes("provider") && h.includes("vendor")),
    );

    console.log("Column mapping:", {
      nameIndex,
      firstNameIndex,
      lastNameIndex,
      npiIndex,
      phoneIndex,
      licenseIndex,
      typeColumnIndex,
    });

    // Process data rows
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!row || row.length === 0) continue;
      const hasAnyCell = row.some(
        (cell) => cell != null && String(cell).trim() !== "",
      );
      if (!hasAnyCell) continue;

      totalDataRows += 1;
      const sheetRowNumber = i + 1; // 1-based including header

      const user: ParsedUser = { id: i };

      // Get name (prefer full name, then combine first/last); normalize ALL CAPS from Excel
      if (nameIndex >= 0 && row[nameIndex]) {
        user.name = formatExcelExtractedName(row[nameIndex].toString());
      } else if (firstNameIndex >= 0 || lastNameIndex >= 0) {
        const firstNameVal =
          firstNameIndex >= 0 && row[firstNameIndex]
            ? formatExcelExtractedName(row[firstNameIndex].toString())
            : "";
        const lastNameVal =
          lastNameIndex >= 0 && row[lastNameIndex]
            ? formatExcelExtractedName(row[lastNameIndex].toString())
            : "";
        if (firstNameVal || lastNameVal) {
          user.name = `${firstNameVal} ${lastNameVal}`.trim();
        }
      }

      // Get other fields - check if the column exists and has data
      if (npiIndex >= 0 && row[npiIndex] && row[npiIndex].toString().trim()) {
        user.npi = row[npiIndex].toString().trim();
      }

      if (ssnIndex >= 0 && row[ssnIndex] && row[ssnIndex].toString().trim()) {
        user.ssn = row[ssnIndex].toString().trim();
      }

      if (
        phoneIndex >= 0 &&
        row[phoneIndex] &&
        row[phoneIndex].toString().trim()
      ) {
        user.phoneNumber = row[phoneIndex].toString().trim();
      }

      if (
        licenseIndex >= 0 &&
        row[licenseIndex] &&
        row[licenseIndex].toString().trim()
      ) {
        user.licenses = [{ number: row[licenseIndex].toString().trim() }];
      }

      user.checkType =
        typeColumnIndex >= 0
          ? parseBulkRowCheckType(row[typeColumnIndex])
          : "Employee";

      if (!isRosterRowReady(user)) {
        skipped.push({
          rowNumber: sheetRowNumber,
          name: "",
          reason: "missing name",
          checkType: user.checkType,
        });
        continue;
      }

      ready.push(user);
    }

    return { ready, skipped, totalDataRows };
  };

  const runBulkAdverseChecks = async (
    currentUsers: ParsedUser[],
    options?: { fromAffableImport?: boolean },
  ) => {
    if (currentUsers.length === 0) {
      setBulkError("No valid records found for exclusion check");
      return;
    }
    setBulkAdverseResultsFromAffableImport(options?.fromAffableImport === true);
    // Start processing: dedicated BulkProcessingView (#10119); close upload modal
    setIsProcessing(true);
    setShowBulkDialog(false);
    setBulkError(null);
    setUploadedUsers(currentUsers);
    const queriedAt = new Date();
    setBulkCompletedAt(null);
    const fileLabel = selectedFile?.name
      ? selectedFile.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ")
      : "";
    setRosterRunLabel(
      fileLabel ||
        (options?.fromAffableImport ? "AffableFLOW import" : "Roster check"),
    );

    // Reset results so progress + bulkResultsReady stay in sync with this run
    const newResults = new Array(currentUsers.length).fill(null);
    setAnalysisResults([...newResults]);
    setCurrentProcessingIndex(0);
    setBulkCompletedCount(0);

    try {
      const { facilityId, orgId } = resolveFacilityIds();
      if (!facilityId) {
        throw new Error("Facility is required for exclusion checks.");
      }
      const subjects = currentUsers.map((user) => {
        const name = String(user.name || "").trim();
        const normalizedSsn = String(user.ssn || "").replace(/\D/g, "");
        if (normalizedSsn && normalizedSsn.length !== 9) {
          throw new Error("SSN must contain exactly 9 digits.");
        }
        const nameParts = name.split(/\s+/).filter(Boolean);
        return {
          name,
          ...(nameParts.length >= 2 && (user.checkType ?? "Employee") !== "Vendor"
            ? { firstName: nameParts[0], lastName: nameParts[nameParts.length - 1] }
            : {}),
          ...(String(user.npi || "").trim() ? { npi: String(user.npi).trim() } : {}),
          ...(normalizedSsn.length === 9 ? { ssn: normalizedSsn } : {}),
          ...(String(user.phoneNumber || "").trim()
            ? { phoneNumber: String(user.phoneNumber).trim() }
            : {}),
          ...(user.licenses?.length ? { licenses: user.licenses } : {}),
          checkType: user.checkType ?? "Employee",
        };
      });
      const started = await oiggService.startBulkAdverseCheck({
        facilityId,
        orgId,
        queriedAt: queriedAt.toISOString(),
        tzAbbrev: getLocalTzAbbrev(queriedAt),
        subjects,
      });

      for (;;) {
        markUserActivity();
        const job = await oiggService.getBulkAdverseCheckJob(started.job_id);
        setBulkCompletedCount(job.completed_count);
        setCurrentProcessingIndex(Math.max(-1, job.completed_count - 1));
        if (job.status === "failed") {
          throw new Error(job.error_message || "Bulk exclusion check failed.");
        }
        if (job.status === "completed") {
          const results = job.response?.results ?? [];
          setAnalysisResults(
            currentUsers.map((user, index) => {
              const result = results[index];
              return {
                success: Boolean(result?.success),
                data: result?.data ?? undefined,
                error: result?.success
                  ? undefined
                  : { message: result?.error?.message || "Adverse check failed" },
                user,
              };
            }),
          );
          setBulkCompletedAt(queriedAt);
          onHistoryPersisted?.();
          break;
        }
        // Keep poll tight so a 4-row SAMPLE_OIG roster can finish within the 4s CI SLA.
        await new Promise((resolve) =>
          setTimeout(resolve, BULK_ADVERSE_CHECK_POLL_INTERVAL_MS),
        );
      }
    } catch (error) {
      if (isOigEntitlementError(error)) {
        setShowOigBlockPicker(true);
        setBulkError(error.message);
        return;
      }
      const message = error instanceof Error ? error.message : "Bulk exclusion check failed.";
      setBulkError(message);
    } finally {
      setIsProcessing(false);
      setCurrentProcessingIndex(-1);
    }
  };

  const bulkResultsReady =
    !isProcessing &&
    uploadedUsers.length > 0 &&
    analysisResults.length === uploadedUsers.length &&
    analysisResults.every((r) => r !== null);

  const bulkCheckedSoFar = isProcessing
    ? bulkCompletedCount
    : analysisResults.filter((r) => r != null).length;

  const performBulkAnalysis = async () => {
    // First check if a file is selected
    if (!selectedFile) {
      setBulkError("Please select an Excel file first");
      return;
    }

    let currentUsers: ParsedUser[] = [];

    try {
      setBulkError(null);
      currentUsers = await processExcelFile(selectedFile);

      if (currentUsers.length === 0) {
        setBulkError(
          "No rows ready to check — every row is missing a name",
        );
        return;
      }
    } catch {
      // Error message already set by processExcelFile
      return;
    }

    await runBulkAdverseChecks(currentUsers);
  };

  const fetchAffableRecords = useCallback(async (workflowType: AffableWorkflowType = selectedAffableWorkflowType) => {
    const orgName = String(resolvedFacility?.org_name || "").trim();
    if (!orgName) {
      setAffableSyncError("Facility organization is unavailable.");
      return;
    }
    setIsSyncingAffableRecords(true);
    setAffableSyncError(null);
    try {
      const workflowMapping =
        await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
          orgName,
          type: workflowType,
        });
      const mappingItem =
        workflowMapping?.success && Array.isArray(workflowMapping.data)
          ? workflowMapping.data[0]
          : undefined;
      const deptId =
        typeof mappingItem?.workflowId === "number"
          ? mappingItem.workflowId
          : undefined;
      const orgId =
        typeof mappingItem?.orgid === "number" ? mappingItem.orgid : undefined;

      const response: ExternalGetRecordAndFilesDetailsResponse =
        await affablebpmIntegrationService.getRecordAndFilesDetails({
          orgName,
          workflowType,
          deptId,
          orgId,
          perPage: 100,
          pageNumber: 0,
        });
      const raw = (response || {}) as {
        Data?: Array<{ RecordList?: Record<string, unknown>[] }>;
        Status?: string;
        ErrorMsg?: string;
        Message?: string;
      };
      if (typeof raw.Status === "string" && raw.Status.toLowerCase() === "failed") {
        setAffableSelectableRecords([]);
        setAffableSyncError(
          raw.ErrorMsg || raw.Message || "Failed to fetch records from AffableFLOW.",
        );
        return;
      }

      const recordList =
        Array.isArray(raw.Data) && Array.isArray(raw.Data[0]?.RecordList)
          ? raw.Data[0]!.RecordList!
          : [];
      const mappingFields: WorkflowMappingField[] | null = Array.isArray(
        (mappingItem as any)?.field_Mapping,
      )
        ? ((mappingItem as any).field_Mapping as WorkflowMappingField[])
        : null;
      // AffableFLOW picker is Vendor/Provider; store as Vendor/Employee for roster Type.
      const importedCheckType: BulkAdverseCheckType =
        workflowType === "Vendor" ? "Vendor" : "Employee";
      const normalized: AffableSelectableRecord[] = recordList.map((r, i) => {
        const base = normalizeAffableRecordToUser(
          (r || {}) as Record<string, unknown>,
          i,
          mappingFields,
        );
        return {
          ...base,
          user: {
            ...base.user,
            checkType: importedCheckType,
          },
        };
      });
      setAffableSelectableRecords(normalized);
      setSelectedAffableRecordKeys([]);
      setAffableTablePage(1);
      if (normalized.length === 0) {
        setAffableSyncError("No records available in AffableFLOW for this facility.");
      }
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "Failed to fetch records from AffableFLOW.";
      setAffableSelectableRecords([]);
      setAffableSyncError(msg);
    } finally {
      setIsSyncingAffableRecords(false);
    }
  }, [normalizeAffableRecordToUser, resolvedFacility?.org_name, selectedAffableWorkflowType]);

  const handleToggleAffableRecord = (key: string) => {
    setSelectedAffableRecordKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleRefreshAffableRecords = async () => {
    // Refresh should reset current Affable-run results and selections.
    setUploadedUsers([]);
    setAnalysisResults([]);
    setBulkAdverseResultsFromAffableImport(false);
    setSelectedAffableRecordKeys([]);
    setBulkError(null);
    await fetchAffableRecords(selectedAffableWorkflowType);
  };

  const handleClearAffableSelectionAndResults = () => {
    setSelectedAffableRecordKeys([]);
    setUploadedUsers([]);
    setAnalysisResults([]);
    setBulkAdverseResultsFromAffableImport(false);
    setBulkError(null);
    setAffableSyncError(null);
    setIsProcessing(false);
    setCurrentProcessingIndex(-1);
  };

  const handleBackToManualEntry = () => {
    setIsAffableImportMode(false);
    setAffableSyncError(null);
    setAffableSelectableRecords([]);
    setSelectedAffableRecordKeys([]);
    setUploadedUsers([]);
    setAnalysisResults([]);
    setBulkAdverseResultsFromAffableImport(false);
    setCurrentProcessingIndex(-1);
    setIsProcessing(false);
  };

  const pagedAffableRecords = useMemo(() => {
    const start = (affableTablePage - 1) * affableRowsPerPage;
    return affableSelectableRecords.slice(start, start + affableRowsPerPage);
  }, [affableSelectableRecords, affableTablePage, affableRowsPerPage]);

  const affableTableTotalPages = Math.max(
    1,
    Math.ceil(affableSelectableRecords.length / affableRowsPerPage),
  );

  useEffect(() => {
    setAffableTablePage((prev) => Math.min(prev, affableTableTotalPages));
  }, [affableTableTotalPages]);

  const handlePerformAdverseCheckForAffable = async () => {
    const selectedUsers = affableSelectableRecords
      .filter((r) => selectedAffableRecordKeys.includes(r.key))
      .map((r) => r.user);
    if (!selectedUsers.length) {
      setAffableSyncError("Select at least one AffableFLOW record.");
      return;
    }
    const readyUsers = selectedUsers.filter((u) => isRosterRowReady(u));
    if (!readyUsers.length) {
      setAffableSyncError(
        "Selected records need a name to run an exclusion check.",
      );
      return;
    }
    setAffableSyncError(null);
    await runBulkAdverseChecks(readyUsers, { fromAffableImport: true });
  };

  const removeFile = () => {
    setSelectedFile(null);
    setSkippedRosterRows([]);
    setRosterParsedRowCount(0);
    setRosterReadyCount(0);
    setUploadedUsers([]);
    setAnalysisResults([]);
    setBulkAdverseResultsFromAffableImport(false);
    setBulkError(null);
  };

  const clearAllUsers = () => {
    setUploadedUsers([]);
    setAnalysisResults([]);
    setBulkAdverseResultsFromAffableImport(false);
    setIsProcessing(false);
    setCurrentProcessingIndex(-1);
    setSelectedFile(null);
    setSkippedRosterRows([]);
    setRosterParsedRowCount(0);
    setRosterReadyCount(0);
    setBulkCompletedAt(null);
    setRosterRunLabel("Roster check");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "98%",
        padding: "16px",
        alignItems: "stretch",
        borderRadius: "12px",
        border: "1px solid #CCF5FF",
        background: "#FFFDFD",
        boxShadow: "0 4px 16px 0 rgba(5, 89, 108, 0.08)",
        overflow: "hidden",
      }}
    >
      {/* Entry Form Accordion (closable) */}
      <StyledAccordion
        expanded
        disableGutters
        onChange={() => {}}
        sx={{
          border: "none",
          boxShadow: "none",
          marginBottom: 0,
        }}
      >
        <AccordionSummary
          sx={{
            padding: "8px",
            minHeight: "48px",
            borderBottom: "1px solid #35d7e4",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              margin: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 500,
                    fontSize: "16px",
                    color: "#065b70",
                  }}
                >
                  Exclusion Check Entry Form
                </Typography>
                <OigCheckQuotaBar
                  snapshot={oigEntitlement}
                  exhausted={oigChecksExhausted}
                  onBuyMore={() => setShowOigBlockPicker(true)}
                />
                <IconButton
                  component="span"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInfoClick(e);
                  }}
                  onMouseEnter={handleInfoMouseEnter}
                  onMouseLeave={handleInfoMouseLeave}
                  aria-label="Information about exclusion and sanction checks"
                  sx={{
                    padding: "4px",
                    color: "#065b70",
                    "&:hover": { backgroundColor: "rgba(5, 91, 112, 0.08)" },
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Rethink Sans, sans-serif",
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "#065b70",
                }}
              >
                Provide the professional details to perform an exclusion check
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {showWatchGuide && (
                <Button
                component="div"
                role="button"
                tabIndex={0}
                disabled={!onWatchGuideClick || watchGuideLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  onWatchGuideClick?.();
                }}
                sx={{
                  display: "flex",
                  padding: "12px 24px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                  "& .MuiButton-startIcon": {
                    marginRight: "4px",
                    marginLeft: 0,
                  },
                  borderRadius: "8px",
                  minHeight: "44px",
                  height: "44px",
                  border: "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                  background: "#FFFFFF",
                  boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#F8FDFF",
                    borderColor: "#065b70",
                    color: "#065b70",
                    boxShadow: "0px 4px 8px rgba(6, 91, 112, 0.2)",
                  },
                  "&:active": {
                    backgroundColor: "#e5f9fd",
                    boxShadow: "0px 1px 2px rgba(6, 91, 112, 0.2)",
                  },
                }}
              >
                {watchGuideIconSrc ? (
                  <Box
                    component="img"
                    src={watchGuideIconSrc}
                    alt="Watch guide"
                    sx={{ width: 16, height: 16, display: "block" }}
                  />
                ) : null}
                <Typography
                  sx={{
                    color: "var(--Brand-Colors-color-brand-hover, #086E86)",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "normal",
                  }}
                >
                  {watchGuideLoading ? "Loading..." : "Watch Guide"}
                </Typography>
                </Button>
              )}
              {!showAffableWorkflowDialog && !isAffableImportMode && (
                <Button
                  component="div"
                  role="button"
                  tabIndex={0}
                  disabled={oigChecksExhausted}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (oigChecksExhausted) {
                      setShowOigBlockPicker(true);
                      return;
                    }
                    handleBulkAdverseCheck();
                  }}
                  sx={{
                    backgroundColor: "#e6fafc",
                    borderRadius: "8px",
                    padding: "4px 12px",
                    minHeight: "32px",
                    height: "32px",
                    textTransform: "none",
                    color: "#086e86",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontWeight: 600,
                    fontSize: "13px",
                    gap: "6px",
                    whiteSpace: "nowrap",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                    boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                    "& .MuiButton-startIcon": {
                      marginRight: "4px",
                      marginLeft: 0,
                    },
                    "&:hover": {
                      backgroundColor: "#f5fdff",
                      borderColor: "#065b70",
                    },
                  }}
                  startIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="17"
                      viewBox="0 0 16 17"
                      fill="none"
                    >
                      <path
                        d="M3.5 2.5C3.22386 2.5 3 2.72386 3 3C3 3.27614 3.22386 3.5 3.5 3.5H12.5C12.7761 3.5 13 3.27614 13 3C13 2.72386 12.7761 2.5 12.5 2.5H3.5ZM8.35355 4.64645C8.15829 4.45118 7.84171 4.45118 7.64645 4.64645L4.14645 8.14645C3.95118 8.34171 3.95118 8.65829 4.14645 8.85355C4.34171 9.04882 4.65829 9.04882 4.85355 8.85355L7.5 6.20711V14C7.5 14.2761 7.72386 14.5 8 14.5C8.27614 14.5 8.5 14.2761 8.5 14V6.20711L11.1464 8.85355C11.3417 9.04882 11.6583 9.04882 11.8536 8.85355C12.0488 8.65829 12.0488 8.34171 11.8536 8.14645L8.35355 4.64645Z"
                        fill="#086E86"
                      />
                    </svg>
                  }
                >
                  {BULK_ADVERSE_CHECK_BUTTON_LABEL}
                </Button>
              )}
              {isAffableConnected && (
                <Button
                  onClick={handleUploadFromAffableFlow}
                  startIcon={<AffableConnectedIcon />}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "8px",
                    padding: "4px 12px",
                    minHeight: "32px",
                    height: "32px",
                    textTransform: "none",
                    color: "var(--Brand-Colors-color-brand-hover, #086E86)",
                    border: "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                    background: "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                    boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: "13px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    "& .MuiButton-startIcon": {
                      marginRight: "4px",
                      marginLeft: 0,
                    },
                    "&:hover": {
                      backgroundColor: "#f5fdff",
                      borderColor: "#065b70",
                      color: "#065b70",
                    },
                  }}
                >
                  Upload from AffableFLOW
                </Button>
              )}
              {isAffableConnected && (
                <Button
                  component="div"
                  role="button"
                  tabIndex={0}
                  variant="outlined"
                  disabled={isConnectingAffable}
                  startIcon={<AffableConnectedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnectAffableBPM();
                  }}
                  sx={{
                    display: "flex",
                    padding: "4px 12px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "8px",
                    minHeight: "32px",
                    height: "32px",
                    border:
                      "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                    background:
                      "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                    boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "& .MuiButton-startIcon": {
                      marginRight: "4px",
                      marginLeft: 0,
                    },
                    "& > svg": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: "#f5fdff",
                      borderColor: "#065b70",
                      color: "#065b70",
                      boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                    },
                    "&:active": {
                      backgroundColor: "#e5f9fd",
                    },
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M16 0H0V16H16V0Z"
                      fill="url(#pattern0_3922_12179)"
                    />
                    <defs>
                      <pattern
                        id="pattern0_3922_12179"
                        patternContentUnits="objectBoundingBox"
                        width="1"
                        height="1"
                      >
                        <use
                          href="#image0_3922_12179"
                          transform="matrix(0.00244772 0 0 0.00237778 0 -0.00290089)"
                        />
                      </pattern>
                      <image
                        id="image0_3922_12179"
                        width="1502"
                        height="423"
                        preserveAspectRatio="none"
                        href="data:image/png;base64,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"
                      />
                    </defs>
                  </svg>
                  <Typography
                    sx={{
                      color: "var(--Brand-Colors-color-brand-hover, #086E86)",
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "13px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "normal",
                      textTransform: "none",
                    }}
                  >
                    {isConnectingAffable
                      ? "Connecting..."
                      : "Connect with AffableFLOW"}
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <Popover
          open={infoPopoverOpen}
          anchorEl={infoAnchorEl}
          onClose={handleInfoPopoverClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              width: 720,
              maxWidth: "95vw",
              p: 0,
              border: "1px solid #5bbfd6",
              borderRadius: "8px",
              backgroundColor: "#FFF",
              boxShadow: "0 3px 8.1px 0 rgba(111, 211, 232, 0.2)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              padding: "12px 12px 0 16px",
              borderBottom: "1px solid #e0e0e0",
              minHeight: "48px",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#065b70",
              }}
            >
              Exclusion & Sanction Check – Information
            </Typography>
            <IconButton
              size="small"
              onClick={handleInfoPopoverClose}
              aria-label="Close"
              sx={{
                color: "#065b70",
                marginTop: -0.5,
                marginRight: -0.5,
                "&:hover": { backgroundColor: "rgba(5, 91, 112, 0.08)" },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              p: 2,
              pt: 1.5,
              userSelect: "text",
              WebkitUserSelect: "text",
              MozUserSelect: "text",
              msUserSelect: "text",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#065b70",
                mb: 0.5,
              }}
            >
              OIG (Office of Inspector General) Exclusions
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, color: "#262626" }}>
              The U.S. Department of Health and Human Services' OIG maintains the List of Excluded Individuals/Entities (LEIE) of people and organizations barred from participating in federally funded healthcare programs (e.g., Medicare, Medicaid, TRICARE) because of misconduct such as fraud or abuse. Anyone on this list cannot receive payment from these programs.
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#065b70",
                mb: 0.5,
              }}
            >
              GSA Exclusions (SAM Debarment/Suspension)
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, color: "#262626" }}>
              The General Services Administration's System for Award Management (SAM) includes individuals and entities debarred or suspended from federal contracts, grants, and assistance. Being listed means the party is ineligible to be awarded or participate in federal procurement or assistance programs.
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#065b70",
                mb: 0.5,
              }}
            >
              Medicare/Medicaid Exclusions & Preclusion
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, color: "#262626" }}>
              Separate from OIG's LEIE, Medicare/Medicaid impose exclusionary actions that prevent payment for services by providers who have been barred from participation or whose enrollment has been revoked. These include lists that deny reimbursement for Part A/B services, Medicare Advantage (MA) items, and Part D drugs when eligibility requirements aren't met.
              We provide state level checks along with the Medicare/Medicaid Opt-Out lists.
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#065b70",
                mb: 0.5,
              }}
            >
              OFAC (Office of Foreign Assets Control) Sanctions
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, color: "#262626" }}>
              OFAC, part of the U.S. Department of the Treasury, administers economic and trade sanctions against foreign countries, entities, and individuals designated for national security or foreign policy reasons. Its sanctions lists — including the Specially Designated Nationals (SDN) list — block U.S. persons from engaging in transactions with listed parties and freeze their U.S.-based assets.
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#065b70",
                mb: 0.5,
              }}
            >
              TRICARE Sanctions/Exclusions
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 0, color: "#262626" }}>
              Under TRICARE (the U.S. military health program), providers excluded by sanction authorities (such as OIG exclusions) are not eligible for payment for services to TRICARE beneficiaries. TRICARE also maintains its own administrative sanction authority to suspend or exclude providers for fraud, abuse, or regulatory violations.
            </Typography>
          </Box>
        </Popover>
        <AccordionDetails sx={{ backgroundColor: "#fffdfd", p: 2 }}>
          {isAffableImportMode ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#065b70",
                    }}
                  >
                    Select records from AffableFLOW ({affableSelectableRecords.length})
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      onClick={handleRefreshAffableRecords}
                      disabled={isSyncingAffableRecords}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        border: "1px solid #5BBFD6",
                        color: "#086E86",
                        backgroundColor: "#FFFFFF",
                        px: 2,
                        "&:hover": { backgroundColor: "#F8FDFF" },
                      }}
                    >
                      {isSyncingAffableRecords ? "Refreshing..." : "Refresh"}
                    </Button>
                    <Button
                      onClick={handleBackToManualEntry}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        border: "1px solid #b3b3b3",
                        color: "#404040",
                        backgroundColor: "#fff",
                        px: 2,
                        "&:hover": { backgroundColor: "#f7f7f7" },
                      }}
                    >
                      Back to Manual Entry
                    </Button>
                  </Box>
                </Box>

                {affableSyncError && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "13px" }}>
                    {affableSyncError}
                  </Typography>
                )}

                {(isSyncingAffableRecords || affableSelectableRecords.length > 0) && (
                  <>
                    <Box
                      sx={{
                        border: "1px solid #dbe7ec",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      {isSyncingAffableRecords ? (
                        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                          <CircularProgress size={22} sx={{ color: "#0a829d" }} />
                        </Box>
                      ) : (
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{ maxHeight: 280, overflow: "auto" }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: "#0A829D" }}>
                                <TableCell
                                  sx={{
                                    width: 56,
                                    backgroundColor: "#0A829D",
                                    color: "#fff",
                                    borderBottom: "1px solid #dbe7ec",
                                  }}
                                >
                                  Select
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: "#0A829D",
                                    color: "#fff",
                                    borderBottom: "1px solid #dbe7ec",
                                  }}
                                >
                                  Name
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: "#0A829D",
                                    color: "#fff",
                                    borderBottom: "1px solid #dbe7ec",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Type
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: "#0A829D",
                                    color: "#fff",
                                    borderBottom: "1px solid #dbe7ec",
                                  }}
                                >
                                  NPI
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: "#0A829D",
                                    color: "#fff",
                                    borderBottom: "1px solid #dbe7ec",
                                  }}
                                >
                                  Phone
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: "#0A829D",
                                    color: "#fff",
                                    borderBottom: "1px solid #dbe7ec",
                                  }}
                                >
                                  License
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {pagedAffableRecords.map((record, index) => (
                                <TableRow
                                  key={record.key}
                                  hover
                                  sx={{
                                    "&:nth-of-type(odd)": { backgroundColor: "#fcfeff" },
                                  }}
                                >
                                  <TableCell sx={{ borderBottom: "1px solid #eef4f7" }}>
                                    <Checkbox
                                      checked={selectedAffableRecordKeys.includes(record.key)}
                                      onChange={() => handleToggleAffableRecord(record.key)}
                                      sx={{ p: 0.5 }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{ borderBottom: "1px solid #eef4f7", fontWeight: 600 }}
                                  >
                                    {record.user.name || `Record ${index + 1}`}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: "1px solid #eef4f7" }}>
                                    {record.user.checkType ?? "Employee"}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: "1px solid #eef4f7" }}>
                                    {record.user.npi || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: "1px solid #eef4f7" }}>
                                    {record.user.phoneNumber || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: "1px solid #eef4f7" }}>
                                    {record.user.licenses?.[0]?.number || "N/A"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      {affableSelectableRecords.length > 0 && (
                        <Box
                          sx={{
                            borderTop: "1px solid #dbe7ec",
                            backgroundColor: "#fff",
                            px: 1.5,
                            py: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: 12,
                              color: "#065b70",
                            }}
                          >
                            Showing{" "}
                            {affableSelectableRecords.length === 0
                              ? 0
                              : (affableTablePage - 1) * affableRowsPerPage + 1}
                            -
                            {Math.min(
                              affableTablePage * affableRowsPerPage,
                              affableSelectableRecords.length,
                            )}{" "}
                            of {affableSelectableRecords.length}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontSize: 12,
                                color: "#065b70",
                              }}
                            >
                              Rows per page
                            </Typography>
                            <Select
                              size="small"
                              value={affableRowsPerPage}
                              onChange={(event) => {
                                setAffableRowsPerPage(Number(event.target.value));
                                setAffableTablePage(1);
                              }}
                              sx={{
                                minWidth: 72,
                                width: 72,
                                height: 28,
                                "& .MuiSelect-select": {
                                  padding: "4px 8px",
                                  fontSize: 12,
                                  display: "flex",
                                  alignItems: "center",
                                },
                              }}
                            >
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                            </Select>
                            <Pagination
                              count={affableTableTotalPages}
                              page={affableTablePage}
                              onChange={(_, value) => setAffableTablePage(value)}
                              color="primary"
                              size="small"
                              sx={{
                                "& .MuiPaginationItem-root": {
                                  fontSize: 12,
                                  height: 28,
                                  minWidth: 28,
                                  padding: "0 6px",
                                },
                                "& .MuiPaginationItem-root.Mui-selected": {
                                  backgroundColor: "#0a829d",
                                  color: "#ffffff",
                                },
                                "& .MuiPaginationItem-root.Mui-selected:hover": {
                                  backgroundColor: "#0a829d",
                                },
                                "& .MuiPaginationItem-previousNext": {
                                  color: "#9ca3af",
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        onClick={handlePerformAdverseCheckForAffable}
                        disabled={
                          isProcessing ||
                          isSyncingAffableRecords ||
                          affableSelectableRecords.length === 0 ||
                          selectedAffableRecordKeys.length === 0
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          px: 3,
                          backgroundColor: "#0a829d",
                          color: "#fff",
                          "&:hover": { backgroundColor: "#065b70" },
                          "&.Mui-disabled": {
                            backgroundColor: "#b0c4c8",
                            color: "#e6fafc",
                          },
                        }}
                      >
                        Perform Exclusion Check
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleClearAffableSelectionAndResults}
                        disabled={isProcessing}
                        sx={{
                          borderColor: "#b3b3b3",
                          color: "#404040",
                          textTransform: "none",
                          borderRadius: "8px",
                          px: 3,
                          "&:hover": {
                            borderColor: "#8c8c8c",
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            ) : uploadedUsers.length === 0 && !result ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  maxWidth: 900,
                  width: "100%",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "18px",
                      color: "#0f172a",
                    }}
                  >
                    Check one person
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontSize: "13px",
                      color: "#64748b",
                      mt: 0.25,
                    }}
                  >
                    Screen a single individual or entity.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    backgroundColor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: isEntitySearch ? "2fr 1fr" : "1fr 1fr 1fr",
                      },
                      gap: 2,
                    }}
                  >
                    {isEntitySearch ? (
                      <CompactFieldGroup>
                        <CompactFieldLabel>
                          Entity Name{" "}
                          <span style={{ color: "#dc2626" }}>*</span>
                        </CompactFieldLabel>
                        <CompactField
                          placeholder="Enter Entity Name"
                          value={entityName}
                          onChange={(e) => setEntityName(e.target.value)}
                          size="small"
                        />
                      </CompactFieldGroup>
                    ) : (
                      <>
                        <CompactFieldGroup>
                          <CompactFieldLabel>
                            Last Name{" "}
                            <span style={{ color: "#dc2626" }}>*</span>
                          </CompactFieldLabel>
                          <CompactField
                            placeholder="Enter Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            size="small"
                          />
                        </CompactFieldGroup>
                        <CompactFieldGroup>
                          <CompactFieldLabel>
                            (and/or) First Name
                          </CompactFieldLabel>
                          <CompactField
                            placeholder="Enter First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            size="small"
                          />
                        </CompactFieldGroup>
                      </>
                    )}
                    <CompactFieldGroup>
                      <CompactFieldLabel>
                        Type <span style={{ color: "#dc2626" }}>*</span>
                      </CompactFieldLabel>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={subjectType}
                          onChange={(e) =>
                            setSubjectType(e.target.value as SubjectType)
                          }
                          sx={{
                            backgroundColor: "#fdffff",
                            borderRadius: "6px",
                            fontFamily: "Rethink Sans, sans-serif",
                            fontSize: "13px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#cbd5e1",
                            },
                          }}
                        >
                          <MenuItem value="Employee">Employee</MenuItem>
                          <MenuItem value="Vendor">Vendor</MenuItem>
                        </Select>
                      </FormControl>
                    </CompactFieldGroup>
                  </Box>

                  <Divider sx={{ borderColor: "#e2e8f0", my: 0.5 }} />

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#0f172a",
                        mb: 0.25,
                      }}
                    >
                      Identifiers
                    </Typography>
                    <Typography
                      sx={{ fontSize: "12px", color: "#64748b", mb: 1.5 }}
                    >
                      Optional — improves match confidence when available.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr 1fr",
                        },
                        gap: 1.5,
                      }}
                    >
                      <CompactFieldGroup>
                        <CompactFieldLabel>NPI</CompactFieldLabel>
                        <CompactField
                          placeholder="10 digits"
                          value={npi}
                          onChange={(e) => setNpi(e.target.value)}
                          size="small"
                          error={Boolean(npiFieldError)}
                          helperText={npiFieldError || undefined}
                          inputProps={{ inputMode: "numeric", maxLength: 10 }}
                        />
                      </CompactFieldGroup>
                      <CompactFieldGroup>
                        <CompactFieldLabel>SSN</CompactFieldLabel>
                        <CompactField
                          placeholder="9 digits (optional)"
                          value={ssn}
                          onChange={(e) => setSsn(e.target.value)}
                          size="small"
                          type="password"
                          inputProps={{ inputMode: "numeric", maxLength: 11 }}
                        />
                      </CompactFieldGroup>
                      <CompactFieldGroup>
                        <CompactFieldLabel>License #</CompactFieldLabel>
                        <CompactField
                          placeholder=""
                          value={license}
                          onChange={(e) => setLicense(e.target.value)}
                          size="small"
                        />
                      </CompactFieldGroup>
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: "#e2e8f0", my: 0.5 }} />

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#0f172a",
                        mb: 1.5,
                      }}
                    >
                      Additional details{" "}
                      <Box
                        component="span"
                        sx={{ fontWeight: 400, color: "#64748b" }}
                      >
                        (optional)
                      </Box>
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr 1fr",
                        },
                        gap: 1.5,
                      }}
                    >
                      <CompactFieldGroup>
                        <CompactFieldLabel>Date of birth</CompactFieldLabel>
                        <CompactField
                          placeholder="YYYY-MM-DD"
                          value={dob}
                          onChange={(e) => setDob(e.target.value)}
                          size="small"
                        />
                      </CompactFieldGroup>
                      <CompactFieldGroup>
                        <CompactFieldLabel>Address</CompactFieldLabel>
                        <CompactField
                          placeholder="Street, City"
                          value={addressLine}
                          onChange={(e) => setAddressLine(e.target.value)}
                          size="small"
                        />
                      </CompactFieldGroup>
                      <CompactFieldGroup>
                        <CompactFieldLabel>State</CompactFieldLabel>
                        <CompactField
                          placeholder="CA"
                          value={addressState}
                          onChange={(e) => setAddressState(e.target.value)}
                          size="small"
                        />
                      </CompactFieldGroup>
                    </Box>
                  </Box>
                </Box>

                {error && (
                  <Typography color="#d32f2f" variant="body2">
                    {error}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    sx={{
                      borderColor: "#b3b3b3",
                      color: "#404040",
                      textTransform: "none",
                      borderRadius: "6px",
                      px: 3,
                      "&:hover": {
                        borderColor: "#8c8c8c",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isValid || submitting || oigChecksExhausted}
                    sx={{
                      textTransform: "none",
                      borderRadius: "6px",
                      px: 3,
                      py: 1.25,
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 500,
                      fontSize: "13px",
                      backgroundColor: "#0e7490",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "#155e75" },
                      "&.Mui-disabled": {
                        backgroundColor: "#b0c4c8",
                        color: "#e6fafc",
                      },
                    }}
                    startIcon={
                      submitting ? (
                        <CircularProgress size={16} sx={{ color: "#e6fafc" }} />
                      ) : null
                    }
                  >
                    {submitting ? "Checking…" : "Run check"}
                  </Button>
                </Box>
              </Box>
            ) : uploadedUsers.length === 0 &&
              result &&
              result.total_exclusions === 0 ? (
              <ClearExclusionResult
                result={result}
                displayName={searchName}
                npi={npi}
                ssn={ssn}
                license={license}
                subjectType={subjectType}
                checkedAt={singleCheckedAt}
                onNewCheck={handleClear}
                onDownload={() => handleDownloadCurrentSingle(result)}
              />
            ) : uploadedUsers.length === 0 &&
              result &&
              result.total_exclusions > 0 ? (
              <ConfirmedExclusionResult
                result={result}
                displayName={searchName}
                npi={npi}
                ssn={ssn}
                subjectType={subjectType}
                dob={dob}
                onNewCheck={handleClear}
                onDownload={() => handleDownloadCurrentSingle(result)}
              />
            ) : isProcessing && uploadedUsers.length > 0 ? (
              <BulkProcessingView
                recordCount={uploadedUsers.length}
                checkedCount={bulkCheckedSoFar}
              />
            ) : bulkResultsReady ? (
              <BulkRosterResultsView
                title={rosterRunLabel}
                completedAt={bulkCompletedAt}
                uploadedCount={
                  rosterParsedRowCount ||
                  uploadedUsers.length + skippedRosterRows.length
                }
                checkedCount={uploadedUsers.length}
                skippedRows={skippedRosterRows}
                uploadedUsers={uploadedUsers}
                analysisResults={analysisResults as AnalysisResult[]}
                onDownload={handleDownloadCurrent}
                onNewCheck={clearAllUsers}
                onAddToAffable={
                  bulkAdverseResultsFromAffableImport && isAffableConnected
                    ? handleAddToAffable
                    : undefined
                }
                isAddingToAffable={isAddingToAffable}
              />
            ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              {/* Processing Progress */}
              {/* {isProcessing && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#065b70" }}
                    >
                      Processing... {currentProcessingIndex + 1} /{" "}
                      {uploadedUsers.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#065b70" }}
                    >
                      {Math.round(
                        ((currentProcessingIndex + 1) / uploadedUsers.length) *
                          100
                      )}
                      %
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      ((currentProcessingIndex + 1) / uploadedUsers.length) *
                      100
                    }
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#e0e7ff",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#0a829d",
                      },
                    }}
                  />
                </Box>
              )} */}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#065b70",
                  }}
                >
                  {uploadedUsers.length} individuals loaded
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {isAffableImportMode && (
                    <Button
                      onClick={handleBackToManualEntry}
                      disabled={isProcessing}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        border: "1px solid #b3b3b3",
                        color: "#404040",
                        backgroundColor: "#fff",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 500,
                        fontSize: "12px",
                        px: 2,
                        py: 0.5,
                        "&:hover": { backgroundColor: "#f7f7f7" },
                      }}
                    >
                      Back to Manual Entry
                    </Button>
                  )}
                  {isAffableConnected && (
                    <Button
                      onClick={handleAddToAffable}
                      disabled={isProcessing || isAddingToAffable}
                      sx={{
                        backgroundColor: "transparent",
                        color: "#0a829d",
                        border: "1px solid #0a829d",
                        textTransform: "none",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 500,
                        fontSize: "12px",
                        px: 2,
                        py: 0.5,
                        borderRadius: "6px",
                        minWidth: "auto",
                        "&:hover": {
                          backgroundColor: "#f0fcff",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#f5f5f5",
                          color: "#9ca3af",
                          border: "1px solid #d1d5db",
                        },
                      }}
                    >
                      {isAddingToAffable ? (
                        <>
                          <CircularProgress
                            size={16}
                            sx={{ color: "#0a829d", mr: 1 }}
                          />
                          Adding...
                        </>
                      ) : (
                        "Add To AffableFLOW"
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={clearAllUsers}
                    disabled={isProcessing}
                    sx={{
                      backgroundColor: "transparent",
                      color: "#dc2626",
                      border: "1px solid #dc2626",
                      textTransform: "none",
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 500,
                      fontSize: "12px",
                      px: 2,
                      py: 0.5,
                      borderRadius: "6px",
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "#fef2f2",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                        color: "#9ca3af",
                        border: "1px solid #d1d5db",
                      },
                    }}
                  >
                    Clear All
                  </Button>
                  {/* <Button
                    onClick={performBulkAnalysis}
                    disabled={uploadedUsers.length === 0 || isProcessing}
                    sx={{
                      backgroundColor:
                        uploadedUsers.length > 0 && !isProcessing
                          ? "#0a829d"
                          : "#b0c4c8",
                      color: "#e6fafc",
                      textTransform: "none",
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "14px",
                      px: 3,
                      py: 1,
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor:
                          uploadedUsers.length > 0 && !isProcessing
                            ? "#065b70"
                            : "#b0c4c8",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#b0c4c8",
                        color: "#e6fafc",
                      },
                    }}
                    startIcon={
                      isProcessing ? (
                        <CircularProgress size={16} sx={{ color: "#e6fafc" }} />
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <circle
                            cx="8"
                            cy="8"
                            r="3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          <path
                            d="M8 1v2M8 13v2M15 8h-2M3 8H1M13.66 13.66l-1.42-1.42M3.76 3.76L2.34 2.34M13.66 2.34l-1.42 1.42M3.76 12.24l-1.42 1.42"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )
                    }
                  >
                    {isProcessing ? "Analyzing..." : "Analyze All Users"}
                  </Button> */}
                </Box>
              </Box>

              {/* Users Table */}
              <Box
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  width: "100%",
                }}
              >
                {/* Table Header */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "160px 110px 110px 110px 100px 110px",
                    gap: 1,
                    p: 2,
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #e5e7eb",
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#374151",
                    //width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    Full Name
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    Phone Number
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    NPI Number
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    License Number
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    Type
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontWeight: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    Status
                  </Typography>
                </Box>

                {/* Table Rows */}
                <Box
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {uploadedUsers.map((user, index) => {
                    const result = analysisResults[index];

                    return (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "160px 110px 110px 110px 100px 110px",
                            gap: 1,
                            p: 2,
                            borderBottom:
                              index < uploadedUsers.length - 1
                                ? "1px solid #f1f5f9"
                                : "none",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            },
                            alignItems: "center",
                            // width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            {user.name || "N/A"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              color: "#6b7280",
                            }}
                          >
                            {user.phoneNumber || "N/A"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              color: "#6b7280",
                            }}
                          >
                            {user.npi || "N/A"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              color: "#6b7280",
                            }}
                          >
                            {user.licenses?.[0]?.number || "N/A"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              color: "#6b7280",
                            }}
                          >
                            {user.checkType ?? "Employee"}
                          </Typography>

                          {/* Status Indicator and Details Button */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            {isProcessing &&
                            currentProcessingIndex === index ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CircularProgress
                                  size={16}
                                  sx={{ color: "#6b7280" }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  Checking...
                                </Typography>
                              </Box>
                            ) : result ? (
                              result.success && result.data ? (
                                result.data.total_exclusions > 0 ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        backgroundColor: "#fbbf24",
                                        borderRadius: "2px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color: "white",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        !
                                      </Typography>
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        color: "#dc2626",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {(() => {
                                        const ssnLabel =
                                          oiggService.formatSsnVerificationLabel(
                                            result.data,
                                          );
                                        return ssnLabel
                                          ? `Exclusion Found (${ssnLabel})`
                                          : "Exclusion Found";
                                      })()}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        backgroundColor: "#10b981",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <svg
                                        width="10"
                                        height="8"
                                        viewBox="0 0 10 8"
                                        fill="none"
                                      >
                                        <path
                                          d="M9 1L3.5 6.5L1 4"
                                          stroke="white"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        color: "#10b981",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {(() => {
                                        const ssnLabel =
                                          oiggService.formatSsnVerificationLabel(
                                            result.data,
                                          );
                                        if (ssnLabel === "No SSN match on LEIE") {
                                          return ssnLabel;
                                        }
                                        return ssnLabel
                                          ? `Verified (${ssnLabel})`
                                          : "Verified";
                                      })()}
                                    </Typography>
                                  </Box>
                                )
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    flexWrap: "wrap",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      backgroundColor: "#fbbf24",
                                      borderRadius: "2px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: "white",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      !
                                    </Typography>
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      color: "#dc2626",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      fontWeight: 600,
                                    }}
                                    title={result.error?.message || "Not found"}
                                  >
                                    {result.error?.message || "Not found"}
                                  </Typography>
                                </Box>
                              )
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#9ca3af",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Pending
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </StyledAccordion>

      {/* Bulk Adverse Check Dialog */}
      <Dialog
        open={showBulkDialog}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseBulkDialog();
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1px solid #d4d4d4",
            maxHeight: "92vh",
            width: "95%",
            maxWidth: "1100px",
            minWidth: { xs: "auto", sm: "720px" },
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#ffffff",
            border: "none",
            p: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 500,
              fontSize: "20px",
              color: "#065b70",
            }}
          >
            Upload roster
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              maxWidth: "1000px",
              minHeight: "420px",
              margin: "0 auto",
            }}
          >
            {/* Hidden file input - always in DOM for re-upload functionality */}
            <input
              type="file"
              id="bulk-file-input"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            {/* Upload Area - Hidden when file is selected */}
            {!selectedFile && (
              <Box
                sx={{
                  minHeight: "420px",
                  border: "2px dashed #a3a3a3",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  transition: "all 0.2s ease-in-out",
                  px: 3,
                  py: 3,
                  "&:hover": {
                    borderColor: "#5bbfd6",
                    backgroundColor: "#f0fcff",
                  },
                }}
                component="label"
                htmlFor="bulk-file-input"
              >
                <Box
                  sx={{
                    backgroundColor: "#e6fafc",
                    border: "1px solid #5bbfd6",
                    borderRadius: "8px",
                    padding: "12px 24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    mb: 1.5,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 21 20"
                    fill="none"
                  >
                    <path
                      d="M10.8536 2.14645C10.6583 1.95118 10.3417 1.95118 10.1464 2.14645L5.64645 6.64645C5.45118 6.84171 5.45118 7.15829 5.64645 7.35355C5.84171 7.54882 6.15829 7.54882 6.35355 7.35355L10 3.70711V12.5C10 12.7761 10.2239 13 10.5 13C10.7761 13 11 12.7761 11 12.5V3.70711L14.6464 7.35355C14.8417 7.54882 15.1583 7.54882 15.3536 7.35355C15.5488 7.15829 15.5488 6.84171 15.3536 6.64645L10.8536 2.14645ZM4.5 9.5C4.5 9.22386 4.27614 9 4 9C3.72386 9 3.5 9.22386 3.5 9.5V14C3.5 15.6569 4.84315 17 6.5 17H14.5C16.1569 17 17.5 15.6569 17.5 14V9.5C17.5 9.22386 17.2761 9 17 9C16.7239 9 16.5 9.22386 16.5 9.5V14C16.5 15.1046 15.6046 16 14.5 16H6.5C5.39543 16 4.5 15.1046 4.5 14V9.5Z"
                      fill="#065B70"
                    />
                  </svg>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 400,
                      fontSize: "18px",
                      color: "#065b70",
                    }}
                  >
                    Upload Documents
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 400,
                    fontSize: "16px",
                    color: "#737373",
                    textAlign: "center",
                    mb: 2,
                  }}
                >
                  Supported file type: XLS only
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 960,
                    minHeight: 256,
                    opacity: 1,
                    borderRadius: 2,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <BulkAdverseUploadExcelPreview />
                </Box>
              </Box>
            )}

            {/* Error Display */}
            {bulkError && <Alert severity="error">{bulkError}</Alert>}

            {/* Selected File Display + validation */}
            {selectedFile && (
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "18px 20px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    pb: 1.75,
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#ecfeff",
                      color: "#0e7490",
                      borderRadius: "6px",
                      fontWeight: 600,
                      fontSize: "12px",
                      flexShrink: 0,
                    }}
                  >
                    XLS
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "12px",
                        color: "#64748b",
                        mt: "2px",
                      }}
                    >
                      {(selectedFile.size / 1024).toFixed(1)} KB
                      {rosterParsedRowCount > 0
                        ? ` · ${rosterParsedRowCount} rows`
                        : ""}
                    </Typography>
                  </Box>
                  <Button
                    onClick={removeFile}
                    sx={{
                      textTransform: "none",
                      color: "#64748b",
                      fontSize: "12px",
                      minWidth: 0,
                      px: 1,
                      "&:hover": { backgroundColor: "#f1f5f9", color: "#0f172a" },
                    }}
                  >
                    Remove
                  </Button>
                </Box>
                {rosterParsedRowCount > 0 && (
                  <Box sx={{ pt: 1.75, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        fontSize: "13px",
                        color: "#047857",
                      }}
                    >
                      <Box component="span" sx={{ width: 18, textAlign: "center", fontWeight: 600 }}>
                        ✓
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: "#047857" }}>
                        {rosterReadyCount} rows have a name and are ready to
                        check
                      </Typography>
                    </Box>
                    {skippedRosterRows.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          fontSize: "13px",
                          color: "#b45309",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box component="span" sx={{ width: 18, textAlign: "center", fontWeight: 600 }}>
                          ⚠
                        </Box>
                        <Typography sx={{ fontSize: "13px", color: "#b45309" }}>
                          {skippedRosterRows.length} rows are missing a name and
                          will be skipped ·{" "}
                          <Box
                            component="button"
                            onClick={downloadSkippedRosterReport}
                            sx={{
                              border: "none",
                              background: "none",
                              p: 0,
                              color: "#0e7490",
                              cursor: "pointer",
                              font: "inherit",
                              textDecoration: "underline",
                            }}
                          >
                            Download error report
                          </Box>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#ffffff",
            borderTop: "none",
            p: 2,
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleCloseBulkDialog}
            sx={{
              backgroundColor: "transparent",
              border: "none",
              borderRadius: "6px",
              padding: "9px 16px",
              textTransform: "none",
              color: "#334155",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 500,
              fontSize: "13px",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={performBulkAnalysis}
            disabled={
              !selectedFile ||
              isProcessing ||
              rosterReadyCount <= 0 ||
              oigChecksExhausted ||
              (oigEntitlement !== null &&
                rosterReadyCount > oigEntitlement.remaining)
            }
            sx={{
              backgroundColor:
                selectedFile && !isProcessing && rosterReadyCount > 0
                  ? "#0e7490"
                  : "#b0c4c8",
              color: "#e6fafc",
              textTransform: "none",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 500,
              fontSize: "13px",
              padding: "9px 16px",
              borderRadius: "6px",
              border: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  selectedFile && !isProcessing && rosterReadyCount > 0
                    ? "#155e75"
                    : "#b0c4c8",
              },
              "&:disabled": {
                backgroundColor: "#b0c4c8",
                color: "#e6fafc",
              },
            }}
            startIcon={
              isProcessing ? (
                <CircularProgress size={16} sx={{ color: "#e6fafc" }} />
              ) : null
            }
          >
            {isProcessing
              ? "Checking…"
              : `Start check on ${rosterReadyCount} rows`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AffableFLOW record type (Vendor / Provider) picker */}
      <Dialog
        open={showAffableWorkflowDialog}
        onClose={() => setShowAffableWorkflowDialog(false)}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1px solid #d4d4d4",
            backgroundColor: "#ffffff",
            width: "min(580px, calc(100vw - 32px))",
            maxWidth: "580px",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#ffffff",
            border: "none",
            boxShadow: "none",
            p: 2,
            pb: 2,
            m: 0,
            borderBottom: "1px solid #e5e5e5",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
              <Typography
                component="span"
                id="affable-record-type-title"
                sx={{
                  display: "block",
                  fontFamily: "Rethink Sans, sans-serif",
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#065b70",
                  lineHeight: 1.35,
                }}
              >
                Choose record type
              </Typography>
              <Typography
                component="span"
                id="affable-record-type-subtitle"
                sx={{
                  display: "block",
                  mt: 0.75,
                  fontFamily: "Rethink Sans, sans-serif",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#737373",
                  lineHeight: 1.45,
                }}
              >
                Select Vendor or Provider record type to perform an exclusion check.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              onClick={() => setShowAffableWorkflowDialog(false)}
              size="small"
              sx={{
                color: "#737373",
                flexShrink: 0,
                mt: -0.25,
                "&:hover": {
                  color: "#065b70",
                  backgroundColor: "rgba(10, 130, 157, 0.08)",
                },
              }}
            >
              <Close sx={{ fontSize: 22 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers={false}
          sx={{
            px: 2,
            pt: 2,
            pb: 3,
            backgroundColor: "#ffffff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 96,
            borderTop: "none",
            borderBottom: "none",
          }}
        >
          <FormControl
            component="div"
            sx={{
              width: "100%",
              m: 0,
              p: 0,
              border: "none",
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <RadioGroup
              row
              aria-labelledby="affable-record-type-title affable-record-type-subtitle"
              name="affable-record-type"
              value={selectedAffableWorkflowType}
              onChange={(e) =>
                setSelectedAffableWorkflowType(e.target.value as AffableWorkflowType)
              }
              sx={{
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 2, sm: 2.5 },
                width: "auto",
                maxWidth: "100%",
              }}
            >
              <FormControlLabel
                value="Vendor"
                control={
                  <Radio
                    size="medium"
                    disableRipple
                    sx={{
                      p: 0.5,
                      color: "#99d4e1",
                      "&.Mui-checked": { color: "#0a829d" },
                      "&:hover": { backgroundColor: "transparent" },
                      "&.Mui-focusVisible": {
                        outline: "2px solid rgba(10, 130, 157, 0.45)",
                        outlineOffset: 2,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "#262626",
                      userSelect: "none",
                    }}
                  >
                    Vendor
                  </Typography>
                }
                sx={{
                  m: 0,
                  mx: 0,
                  alignItems: "center",
                  gap: 0.75,
                  py: 0.5,
                  px: 0.5,
                  border: "none",
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              />
              <FormControlLabel
                value="Provider"
                control={
                  <Radio
                    size="medium"
                    disableRipple
                    sx={{
                      p: 0.5,
                      color: "#99d4e1",
                      "&.Mui-checked": { color: "#0a829d" },
                      "&:hover": { backgroundColor: "transparent" },
                      "&.Mui-focusVisible": {
                        outline: "2px solid rgba(10, 130, 157, 0.45)",
                        outlineOffset: 2,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "#262626",
                      userSelect: "none",
                    }}
                  >
                    Provider
                  </Typography>
                }
                sx={{
                  m: 0,
                  mx: 0,
                  alignItems: "center",
                  gap: 0.75,
                  py: 0.5,
                  px: 0.5,
                  border: "none",
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#ffffff",
            borderTop: "none",
            p: 2,
            pt: 0,
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setShowAffableWorkflowDialog(false)}
            sx={{
              backgroundColor: "white",
              border: "1px solid #b3b3b3",
              borderRadius: "8px",
              padding: "12px 24px",
              textTransform: "none",
              color: "#262626",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 600,
              fontSize: "14px",
              boxShadow: "0px 4px 4px 0px rgba(194,194,194,0.25)",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setShowAffableWorkflowDialog(false);
              await startAffableImportMode(selectedAffableWorkflowType);
            }}
            sx={{
              backgroundColor: "#0a829d",
              color: "#e6fafc",
              textTransform: "none",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 600,
              fontSize: "14px",
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              "&:hover": {
                backgroundColor: "#065b70",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legacy results accordion — hide during bulk processing (#10119) */}
      {result &&
      uploadedUsers.length > 0 &&
      !bulkResultsReady &&
      !isProcessing ? (
        <StyledAccordion
          expanded
          disableGutters
          onChange={() => {}}
          sx={{ backgroundColor: "#FFFFFF" }}
        >
          <AccordionSummary
            sx={{
              backgroundColor: "#FFFFFF",
              padding: "8px",
              minHeight: "48px",
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                margin: 0,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
              >
                <path
                  d="M1.5 0C0.671573 0 0 0.671573 0 1.5V14.5C0 15.3284 0.671573 16 1.5 16H8.5C9.32843 16 10 15.3284 10 14.5V13C10 12.7238 9.77599 12.4998 9.49985 12.4998C9.08657 12.4998 8.82263 12.398 8.64373 12.2639C8.46125 12.127 8.32203 11.9223 8.21988 11.6414C8.00592 11.053 7.99993 10.2742 7.99985 9.49973C7.99984 9.36714 7.94716 9.23999 7.8534 9.14623L7.5666 8.85933C7.45782 8.75044 7.3251 8.61759 6.35356 7.64618C5.88667 7.17934 5.75014 6.86588 5.72435 6.69089C5.70385 6.55183 5.74583 6.45651 5.84563 6.361C6.06033 6.15555 6.21264 6.01659 6.38518 5.97463C6.48906 5.94937 6.72441 5.93132 7.14647 6.35333L10.1464 9.35331C10.3417 9.54857 10.6583 9.54857 10.8536 9.35331C11.0488 9.15805 11.0488 8.84146 10.8536 8.6462L10 7.79263V4.70711L12.5607 7.26777C12.842 7.54907 13 7.9306 13 8.32843V15.5C13 15.7761 13.2239 16 13.5 16C13.7761 16 14 15.7761 14 15.5V8.32843C14 7.66539 13.7366 7.0295 13.2678 6.56066L10 3.29289V1.5C10 0.671573 9.32843 0 8.5 0H1.5ZM9 3.49951C9 3.49918 9 3.49984 9 3.49951V6.79264L7.85353 5.64618C7.27551 5.06823 6.7003 4.86887 6.14891 5.00294C6.03719 5.03011 5.9327 5.07032 5.83605 5.11804C5.57067 5.04119 5.29015 5 5 5C3.34315 5 2 6.34315 2 8C2 9.65685 3.34315 11 5 11C5.77173 11 6.47541 10.7086 7.00699 10.2298C7.02414 10.8077 7.0824 11.4395 7.28009 11.9831C7.42795 12.3897 7.66371 12.7788 8.04369 13.0638L8.05012 13.0686C7.44149 13.2597 7 13.8283 7 14.5V15H3V14.5C3 13.6716 2.32843 13 1.5 13H1V3H1.5C2.32843 3 3 2.32843 3 1.5V1H7V1.5C7 2.32843 7.67157 3 8.5 3H9V3.49951ZM8.99985 14.4998L8.99993 14.5086C8.99534 14.7808 8.77327 15 8.5 15H8V14.5C8 14.2239 8.22386 14 8.5 14H8.99985V14.4998ZM3 8C3 6.93775 3.82814 6.06891 4.87401 6.0039C4.75028 6.23482 4.68743 6.51371 4.73503 6.8367C4.80612 7.31904 5.11334 7.82022 5.6465 8.35332L6.55324 9.26004C6.18654 9.71151 5.62696 10 5 10C3.89543 10 3 9.10457 3 8ZM2 1V1.5C2 1.77614 1.77614 2 1.5 2H1V1.5C1 1.22386 1.22386 1 1.5 1H2ZM1 14H1.5C1.77614 14 2 14.2239 2 14.5V15H1.5C1.22386 15 1 14.7761 1 14.5V14ZM9 2H8.5C8.22386 2 8 1.77614 8 1.5V1H8.5C8.77614 1 9 1.22386 9 1.5V2Z"
                  fill="#065B70"
                />
              </svg>
              <Typography
                sx={{
                  fontFamily: "Rethink Sans, sans-serif",
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#065B70",
                }}
              >
                Exclusion Check Results
              </Typography>
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadCurrentSingle(result);
                }}
                sx={{
                  ml: "auto",
                  display: "flex",
                  padding: "8px 12px",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "8px",
                  border: "1px solid #5BBFD6",
                  color: "#065B70",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#EAF8FB",
                    border: "1px solid #5BBFD6",
                  },
                }}
              >
                Download XLS
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "#FFFFFF", pr: 1 }}>
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Accordion
                expanded
                disableGutters
                onChange={() => {}}
                sx={{
                  backgroundColor: "rgb(249, 254, 255)",
                  border: "1px solid #9EE5F4",
                  borderRadius: "4px",
                  boxShadow: "0px 4px 8px 0px rgba(111,211,232,0.15)",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  sx={{
                    backgroundColor: "rgb(249, 254, 255)",
                    borderBottom: "1px solid #9EE5F4",
                    cursor: "default",
                    padding: "8px",
                    minHeight: "48px",
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      margin: 0,
                    },
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600, color: "rgb(6, 91, 112)" }}
                  >
                    Adverse Check Summary
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#ffffff" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 2,
                    }}
                  >
                    <SummaryItem
                      label="Name"
                      value={
                        result?.items?.[0]?.names?.[0]
                          ?.fullName || searchName
                      }
                    />
                    <SummaryItem
                      label="NPI"
                      value={result?.items?.[0]?.npi || npi}
                    />
                    <SummaryItem
                      label="Match Score"
                      value={String(
                        result?.items?.[0]?.score ?? "-"
                      )}
                    />
                    <SummaryItem
                      label="Total Exclusions"
                      value={String(result?.total_exclusions ?? 0)}
                    />
                    <SummaryItem label="Code" value={result?.code || ""} />
                    <SummaryItem
                      label="Message"
                      value={result?.message || ""}
                      fullWidth
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded
                disableGutters
                onChange={() => {}}
                sx={{
                  backgroundColor: "rgb(249, 254, 255)",
                  border: "1px solid #9EE5F4",
                  borderRadius: "4px",
                  boxShadow: "0px 4px 8px 0px rgba(111,211,232,0.15)",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  sx={{
                    backgroundColor: "rgb(249, 254, 255)",
                    borderBottom: "1px solid #9EE5F4",
                    cursor: "default",
                    padding: "8px",
                    minHeight: "48px",
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      margin: 0,
                    },
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600, color: "rgb(6, 91, 112)" }}
                  >
                    Exclusion Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#ffffff" }}>
                  {result && renderExclusions(result)}
                </AccordionDetails>
              </Accordion>
            </Box> */}
            {renderResultUI(result, { npi, ssn })}
          </AccordionDetails>
        </StyledAccordion>
      ) : null}
      <OigCheckBlockPicker
        open={showOigBlockPicker}
        onClose={() => setShowOigBlockPicker(false)}
      />
    </Box>
  );
}

function buildMatchedOnLabel(
  item: AdverseCheckData["items"][number] | undefined,
  criteria: { npi?: string; ssn?: string; dob?: string; license?: string },
): string {
  const parts: string[] = ["Name"];
  if (String(criteria.npi || "").trim()) parts.push("NPI");
  if (criteria.dob?.trim()) parts.push("DOB");
  else if (criteria.ssn?.trim()) parts.push("SSN");
  else if (String(criteria.license || "").trim()) parts.push("License");
  const score =
    item?.score != null && Number.isFinite(Number(item.score))
      ? ` (${item.score}%)`
      : "";
  return `${parts.join(" + ")}${score}`;
}

function buildMatchChipLabel(
  item: AdverseCheckData["items"][number] | undefined,
  criteria: { npi?: string; ssn?: string; dob?: string; license?: string },
): string {
  return `Match: ${buildMatchedOnLabel(item, criteria)}`;
}

function formatBulkCheckedAt(date: Date | null): string {
  if (!date) return "";
  try {
    return date.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, " UTC");
  } catch {
    return "";
  }
}

function bulkSubjectMeta(user: ParsedUser): string {
  const parts: string[] = [];
  if (user.checkType) parts.push(user.checkType);
  if (user.npi?.trim()) parts.push(`NPI ${user.npi.trim()}`);
  else if (user.licenses?.[0]?.number) {
    parts.push(`License ${user.licenses[0].number}`);
  }
  return parts.join(" · ");
}

/** Flat detail rows for roster confirmed-exclusion cards (mockup bulk-results). */
function BulkRosterExclusionDetails({
  result,
  npi,
  ssn,
  license,
}: {
  result: AdverseCheckData;
  npi?: string;
  ssn?: string;
  license?: string;
}) {
  const mergedCards = mergeExclusionsBySharedFields(
    oiggService.getAllExclusions(result),
  );
  if (mergedCards.length === 0) {
    return (
      <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
        No exclusion records.
      </Typography>
    );
  }

  const detailRow = (label: string, value: React.ReactNode, mono?: boolean) => (
    <React.Fragment key={label}>
      <Typography sx={{ color: "#64748b", fontSize: "13px" }}>{label}</Typography>
      <Typography
        sx={{
          color: "#1e293b",
          fontSize: "13px",
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
            : "inherit",
        }}
      >
        {value}
      </Typography>
    </React.Fragment>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {mergedCards.map((card, idx) => {
        const { exclusion, professions, sourceUpdatedLines } = card;
        const parentItem = result.items?.find((item) =>
          (item?.attributes?.exclusions || []).some(
            (ex) => exclusionMergeKey(ex) === exclusionMergeKey(exclusion),
          ),
        );
        const reinstatement = exclusion.reinstatementDate
          ? exclusion.reinstatementDate
          : "Indefinite";
        const professionLine =
          professions.length > 0 ? professions.join(", ") : "";
        return (
          <Box
            key={idx}
            sx={{
              display: "grid",
              gridTemplateColumns: "140px 1fr",
              gap: "8px 16px",
            }}
          >
            {detailRow(
              "Matched on",
              buildMatchedOnLabel(parentItem, { npi, ssn, license }),
            )}
            {detailRow("Source", exclusionListLabel(exclusion))}
            {detailRow("Excluded date", exclusion.excludedDate || "—")}
            {detailRow("Reinstatement", reinstatement)}
            {professionLine
              ? detailRow("Profession", professionLine)
              : null}
            {detailRow("Address", composeExclusionAddress(exclusion))}
            {sourceUpdatedLines.length > 0
              ? detailRow(
                  "Source updated",
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {sourceUpdatedLines.map((line) => (
                      <Typography
                        key={line}
                        component="span"
                        sx={{
                          fontSize: "13px",
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>,
                  true,
                )
              : null}
          </Box>
        );
      })}
    </Box>
  );
}

function bulkClearStatusLabel(data: AdverseCheckData): string {
  const ssnLabel = oiggService.formatSsnVerificationLabel(data);
  let statusLabel =
    data.code === "not_found" ? "No exclusions detected" : "Clear";
  if (ssnLabel === "No SSN match on LEIE") {
    statusLabel = "No SSN match on LEIE";
  } else if (ssnLabel) {
    statusLabel = `${statusLabel} (${ssnLabel})`;
  }
  return statusLabel;
}

/** Exported for unit tests (AB#10332 clear roster rows + accordion). */
export function BulkRosterResultsView({
  title,
  completedAt,
  uploadedCount,
  checkedCount,
  skippedRows,
  uploadedUsers,
  analysisResults,
  onDownload,
  onNewCheck,
  onAddToAffable,
  isAddingToAffable,
}: {
  title: string;
  completedAt: Date | null;
  uploadedCount: number;
  checkedCount: number;
  skippedRows: SkippedRosterRow[];
  uploadedUsers: ParsedUser[];
  analysisResults: AnalysisResult[];
  onDownload: () => void;
  onNewCheck: () => void;
  onAddToAffable?: () => void;
  isAddingToAffable?: boolean;
}) {
  const clearIndexes = analysisResults
    .map((r, i) =>
      r.success && r.data && r.data.total_exclusions === 0 ? i : -1,
    )
    .filter((i) => i >= 0);
  const exclusionIndexes = analysisResults
    .map((r, i) =>
      r.success && r.data && r.data.total_exclusions > 0 ? i : -1,
    )
    .filter((i) => i >= 0);
  const firstPanelKey =
    clearIndexes.length > 0
      ? `clear-${clearIndexes[0]}`
      : exclusionIndexes.length > 0
        ? `excl-${exclusionIndexes[0]}`
        : null;
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    firstPanelKey ? { [firstPanelKey]: true } : {},
  );

  const clearCount = clearIndexes.length;
  const errorCount = analysisResults.filter((r) => !r.success).length;
  const skippedCount = skippedRows.length;
  const checkedAt = formatBulkCheckedAt(completedAt);

  const toggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Box>
        <Button
          onClick={onNewCheck}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            color: "#334155",
            fontSize: "13px",
            fontWeight: 500,
            px: 1,
            mb: 1.5,
            minWidth: 0,
            "&:hover": { backgroundColor: "#f1f5f9" },
          }}
        >
          ← New check
        </Button>
        <Typography
          sx={{ fontWeight: 600, fontSize: "18px", color: "#0f172a" }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#64748b", mt: 0.25 }}>
          {uploadedCount} records uploaded · {checkedCount} checked
          {checkedAt ? ` · ${checkedAt}` : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        {[
          { n: clearCount, l: "Clear", tone: "good" as const },
          {
            n: exclusionIndexes.length,
            l: "Confirmed exclusion",
            tone: "bad" as const,
          },
          { n: skippedCount, l: "Skipped", tone: "skip" as const },
          { n: errorCount, l: "Errors", tone: "neutral" as const },
        ].map((stat) => (
          <Box
            key={stat.l}
            sx={{
              p: "18px 20px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: 600,
                lineHeight: 1,
                mb: 0.75,
                color:
                  stat.tone === "good"
                    ? "#047857"
                    : stat.tone === "bad"
                      ? "#b91c1c"
                      : stat.tone === "skip"
                        ? "#b45309"
                        : "#0f172a",
              }}
            >
              {stat.n}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 600,
              }}
            >
              {stat.l}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {onAddToAffable && (
          <Button
            variant="outlined"
            onClick={onAddToAffable}
            disabled={isAddingToAffable}
            sx={{
              textTransform: "none",
              borderColor: "#0e7490",
              color: "#0e7490",
              borderRadius: "6px",
              fontWeight: 500,
              fontSize: "13px",
            }}
          >
            {isAddingToAffable ? "Adding…" : "Add to AffableFLOW"}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onDownload}
          sx={{
            textTransform: "none",
            backgroundColor: "#0e7490",
            borderRadius: "6px",
            fontWeight: 500,
            fontSize: "13px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#155e75" },
          }}
        >
          ↓ Download XLS
        </Button>
      </Box>

      {clearCount > 0 && (
        <Box data-testid="bulk-clear-results">
          <Typography
            sx={{ fontWeight: 600, fontSize: "16px", color: "#0f172a", mb: 1.5 }}
          >
            Clear
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {clearIndexes.map((index) => {
              const result = analysisResults[index];
              const user = uploadedUsers[index];
              const panelKey = `clear-${index}`;
              const isOpen = Boolean(expanded[panelKey]);
              const name =
                result.data?.items?.[0]?.names?.[0]?.fullName ||
                user?.name ||
                `Record ${index + 1}`;
              const statusLabel = result.data
                ? bulkClearStatusLabel(result.data)
                : "Clear";
              return (
                <Box
                  key={panelKey}
                  data-testid={`bulk-clear-row-${index}`}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    onClick={() => toggle(panelKey)}
                    sx={{
                      px: 2.25,
                      py: 1.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontSize: "10px",
                        transform: isOpen ? "rotate(90deg)" : "none",
                        transition: "transform 0.15s",
                        width: 12,
                      }}
                    >
                      ▶
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#0f172a",
                        }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "12px", color: "#64748b", mt: 0.25 }}
                      >
                        {bulkSubjectMeta(user || { id: index })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "999px",
                        backgroundColor: "#ecfdf5",
                        color: "#047857",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: "#059669",
                        }}
                      />
                      Clear
                    </Box>
                  </Box>
                  {isOpen && (
                    <Box
                      sx={{
                        px: 2.25,
                        py: 2,
                        borderTop: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", color: "#334155" }}>
                        {statusLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {exclusionIndexes.length > 0 && (
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "16px", color: "#0f172a", mb: 1.5 }}
          >
            Confirmed exclusions
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {exclusionIndexes.map((index) => {
              const result = analysisResults[index];
              const user = uploadedUsers[index];
              const panelKey = `excl-${index}`;
              const isOpen = Boolean(expanded[panelKey]);
              const name =
                result.data?.items?.[0]?.names?.[0]?.fullName ||
                user?.name ||
                `Record ${index + 1}`;
              return (
                <Box
                  key={panelKey}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    onClick={() => toggle(panelKey)}
                    sx={{
                      px: 2.25,
                      py: 1.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontSize: "10px",
                        transform: isOpen ? "rotate(90deg)" : "none",
                        transition: "transform 0.15s",
                        width: 12,
                      }}
                    >
                      ▶
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#0f172a",
                        }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "12px", color: "#64748b", mt: 0.25 }}
                      >
                        {bulkSubjectMeta(user || { id: index })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "999px",
                        backgroundColor: "#fef2f2",
                        color: "#b91c1c",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: "#dc2626",
                        }}
                      />
                      Confirmed
                    </Box>
                  </Box>
                  {isOpen && result.data && (
                    <Box
                      sx={{
                        px: 2.25,
                        py: 2,
                        borderTop: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <BulkRosterExclusionDetails
                        result={result.data}
                        npi={user?.npi}
                        ssn={user?.ssn}
                        license={user?.licenses?.[0]?.number}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {skippedCount > 0 && (
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "16px", color: "#0f172a" }}
          >
            Skipped rows
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#64748b", mt: 0.25, mb: 1.5 }}>
            Missing a name — not checked. Included in export with status
            &quot;Skipped&quot;.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {skippedRows.map((row) => (
              <Box
                key={`${row.rowNumber}-${row.name}`}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  px: 2.25,
                  py: 1.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box sx={{ width: 12 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#0f172a",
                    }}
                  >
                    {row.name || "Unnamed row"}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "12px", color: "#64748b", mt: 0.25 }}
                  >
                    {row.checkType || "Employee"} · row {row.rowNumber} —{" "}
                    {row.reason}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: "999px",
                    backgroundColor: "#fffbeb",
                    color: "#b45309",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#d97706",
                    }}
                  />
                  Skipped
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {errorCount > 0 && (
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "16px", color: "#0f172a", mb: 1.5 }}
          >
            Errors
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {analysisResults.map((result, index) => {
              if (result.success) return null;
              const user = uploadedUsers[index];
              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    px: 2.25,
                    py: 1.75,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}
                  >
                    {user?.name || `Record ${index + 1}`}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#b91c1c", mt: 0.5 }}>
                    {result.error?.message || "Check failed"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** Merged match-record cards (shared by single-check + roster bulk results). */
function MergedExclusionMatchCards({
  result,
  npi,
  ssn,
  dob,
}: {
  result: AdverseCheckData;
  npi?: string;
  ssn?: string;
  dob?: string;
}) {
  const exclusions = oiggService.getAllExclusions(result);
  const mergedCards = mergeExclusionsBySharedFields(exclusions);
  if (mergedCards.length === 0) {
    return (
      <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>
        No exclusion records.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {mergedCards.map((card, idx) => {
        const {
          exclusion,
          displayName: cardName,
          professions,
          sourceUpdatedLines,
        } = card;
        const parentItem = result.items?.find((item) =>
          (item?.attributes?.exclusions || []).some(
            (ex) => exclusionMergeKey(ex) === exclusionMergeKey(exclusion),
          ),
        );
        const sourceLabel = exclusionListLabel(exclusion);
        const reinstatement = exclusion.reinstatementDate
          ? exclusion.reinstatementDate
          : "No reinstatement date — indefinite";
        const professionLine =
          professions.length > 0 ? professions.join(", ") : "—";
        return (
          <Box
            key={idx}
            sx={{
              border: "1px solid #dc2626",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2.25,
                py: 1.75,
                backgroundColor: "#fef2f2",
                borderBottom: "1px solid #fecaca",
              }}
            >
              <Typography
                sx={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}
              >
                {cardName}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: "4px",
                    backgroundColor: "#f1f5f9",
                    color: "#334155",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                >
                  {buildMatchChipLabel(parentItem, { npi, ssn, dob })}
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: "4px",
                    backgroundColor: "#ecfeff",
                    color: "#155e75",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                >
                  Source: {sourceLabel}
                </Box>
              </Box>
            </Box>
            <Box sx={{ p: 2.25, backgroundColor: "#fff" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: "8px 16px",
                  fontSize: "13px",
                }}
              >
                <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
                  Profession
                </Typography>
                <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
                  {professionLine}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
                  Licenses
                </Typography>
                <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
                  {exclusion.licenseNumber || "—"}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
                  Address
                </Typography>
                <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
                  {composeExclusionAddress(exclusion)}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
                  Excluded date
                </Typography>
                <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
                  {exclusion.excludedDate || "—"}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
                  Reinstatement
                </Typography>
                <Typography
                  sx={{
                    color: exclusion.reinstatementDate ? "#1e293b" : "#64748b",
                    fontSize: "13px",
                  }}
                >
                  {reinstatement}
                </Typography>
                {sourceUpdatedLines.length > 0 && (
                  <>
                    <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
                      Source updated
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {sourceUpdatedLines.map((line) => (
                        <Typography
                          key={line}
                          sx={{
                            color: "#1e293b",
                            fontSize: "13px",
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/** Single-check clear result (#10116) — green twin of ConfirmedExclusionResult. */
function ClearExclusionResult({
  result,
  displayName,
  npi,
  ssn,
  license,
  subjectType,
  checkedAt,
  onNewCheck,
  onDownload,
}: {
  result: AdverseCheckData;
  displayName: string;
  npi: string;
  ssn: string;
  license: string;
  subjectType: SubjectType;
  checkedAt: Date | null;
  onNewCheck: () => void;
  onDownload: () => void;
}) {
  const [showAllFreshness, setShowAllFreshness] = useState(false);
  const headerName =
    displayName ||
    result.items?.[0]?.names?.[0]?.fullName ||
    "Checked subject";
  const subtitle = buildClearResultSubtitle({
    npi: String(result.items?.[0]?.npi || npi || "").trim() || npi,
    ssn,
    license,
    subjectType,
  });
  const checkedLine = formatCheckedAtLine(
    checkedAt,
    formatOperatorName(getCurrentUser()),
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        maxWidth: 820,
        width: "100%",
      }}
    >
      <Button
        onClick={onNewCheck}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          color: "#334155",
          fontSize: "13px",
          fontWeight: 500,
          px: 1,
          minWidth: 0,
          "&:hover": { backgroundColor: "#f1f5f9" },
        }}
      >
        ← New check
      </Button>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          p: 3,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: "#ecfdf5",
              color: "#047857",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            ✓
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#0f172a",
                }}
              >
                {headerName}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "999px",
                  backgroundColor: "#ecfdf5",
                  color: "#047857",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#059669",
                  }}
                />
                No matches found
              </Box>
            </Box>
            <Typography sx={{ fontSize: "13px", color: "#64748b", mt: 0.5 }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#e2e8f0" }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "150px 1fr",
            gap: "8px 16px",
            fontSize: "13px",
            alignItems: "start",
          }}
        >
          <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
            Sources checked
          </Typography>
          <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
            {EXCLUSION_SOURCES_CHECKED}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
            Source freshness
          </Typography>
          <Box sx={{ color: "#1e293b", fontSize: "13px" }}>
            {showAllFreshness ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {EXCLUSION_SOURCE_FRESHNESS_ALL.map((line) => (
                  <Typography key={line} sx={{ color: "#1e293b", fontSize: "13px" }}>
                    {line}
                  </Typography>
                ))}
                <Button
                  onClick={() => setShowAllFreshness(false)}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    color: "#0e7490",
                    fontSize: "13px",
                    fontWeight: 500,
                    p: 0,
                    minWidth: 0,
                    "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                  }}
                >
                  show less
                </Button>
              </Box>
            ) : (
              <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
                {EXCLUSION_SOURCE_FRESHNESS_PREVIEW}
                {" · "}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowAllFreshness(true)}
                  sx={{
                    border: "none",
                    background: "none",
                    padding: 0,
                    color: "#0e7490",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  see all
                </Box>
              </Typography>
            )}
          </Box>
          <Typography sx={{ color: "#64748b", fontSize: "13px" }}>
            Checked at
          </Typography>
          <Typography sx={{ color: "#1e293b", fontSize: "13px" }}>
            {checkedLine || "—"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onDownload}
          sx={{
            textTransform: "none",
            borderColor: "#0e7490",
            color: "#0e7490",
            borderRadius: "6px",
            fontWeight: 500,
            fontSize: "13px",
            "&:hover": { borderColor: "#155e75", backgroundColor: "#ecfeff" },
          }}
        >
          ↓ Download XLS
        </Button>
        <Button
          variant="contained"
          onClick={onNewCheck}
          sx={{
            textTransform: "none",
            backgroundColor: "#0e7490",
            borderRadius: "6px",
            fontWeight: 500,
            fontSize: "13px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#155e75" },
          }}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
}

function ConfirmedExclusionResult({
  result,
  displayName,
  npi,
  ssn,
  subjectType,
  dob,
  onNewCheck,
  onDownload,
}: {
  result: AdverseCheckData;
  displayName: string;
  npi: string;
  ssn: string;
  subjectType: SubjectType;
  dob: string;
  onNewCheck: () => void;
  onDownload: () => void;
}) {
  const headerName =
    displayName ||
    result.items?.[0]?.names?.[0]?.fullName ||
    "Checked subject";
  const metaNpi = String(result.items?.[0]?.npi || npi || "").trim();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        maxWidth: 820,
        width: "100%",
      }}
    >
      <Button
        onClick={onNewCheck}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          color: "#334155",
          fontSize: "13px",
          fontWeight: 500,
          px: 1,
          minWidth: 0,
          "&:hover": { backgroundColor: "#f1f5f9" },
        }}
      >
        ← New check
      </Button>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          p: 3,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: "#fef2f2",
              color: "#b91c1c",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            !
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#0f172a",
                }}
              >
                {headerName}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "999px",
                  backgroundColor: "#fef2f2",
                  color: "#b91c1c",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#dc2626",
                  }}
                />
                Confirmed exclusion
              </Box>
            </Box>
            <Typography sx={{ fontSize: "13px", color: "#64748b", mt: 0.5 }}>
              {metaNpi ? `NPI ${metaNpi} · ` : ""}
              {subjectType}
            </Typography>
          </Box>
        </Box>
      </Box>

      <MergedExclusionMatchCards
        result={result}
        npi={npi}
        ssn={ssn}
        dob={dob}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onDownload}
          sx={{
            textTransform: "none",
            borderColor: "#0e7490",
            color: "#0e7490",
            borderRadius: "6px",
            fontWeight: 500,
            fontSize: "13px",
            "&:hover": { borderColor: "#155e75", backgroundColor: "#ecfeff" },
          }}
        >
          ↓ Download XLS
        </Button>
        <Button
          variant="contained"
          onClick={onNewCheck}
          sx={{
            textTransform: "none",
            backgroundColor: "#0e7490",
            borderRadius: "6px",
            fontWeight: 500,
            fontSize: "13px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#155e75" },
          }}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
}

const renderResultUI = (result: AdverseCheckData, criteria: { npi?: string; ssn?: string }) => {
  const hasExclusions = result.total_exclusions > 0;

  // -------------------------------
  // Case: NO EXCLUSIONS FOUND
  // -------------------------------
  if (!hasExclusions) {
    // found = person in DB with no exclusions; not_found = no exclusions detected
    const ssnLabel = oiggService.formatSsnVerificationLabel(result);
    let statusLabel =
      result.code === "not_found" ? "No exclusions detected" : "Verified Clean";
    if (ssnLabel === "No SSN match on LEIE") {
      statusLabel = "No SSN match on LEIE";
    } else if (ssnLabel) {
      statusLabel = `${statusLabel} (${ssnLabel})`;
    }
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
        }}
      >
        {/* Name + Verified */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2, mb: 1 }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 14,
              minWidth: 60,
              color: "#374151",
            }}
          >
            Name:
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
            {result.items?.[0]?.names?.[0]?.fullName || "N/A"}
          </Typography>
          <Box
            sx={{
              backgroundColor: "#dcfce7",
              border: "1px solid #16a34a",
              borderRadius: "16px",
              px: 2,
              py: 0.5,
              ml: 1,
            }}
          >
            <Typography
              sx={{ fontWeight: 500, fontSize: 12, color: "#166534" }}
            >
              {statusLabel}
            </Typography>
          </Box>
        </Box>

        {/* Result Row */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2, mb: 1 }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 14,
              minWidth: 60,
              color: "#374151",
            }}
          >
            Result:
          </Typography>
          <Box
            sx={{
              backgroundColor: "#dcfce7",
              border: "1px solid #16a34a",
              borderRadius: "16px",
              px: 2,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#16a34a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path
                  d="M9 1L3.5 6.5L1 4"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Typography
              sx={{ fontWeight: 500, fontSize: 12, color: "#166534" }}
            >
              No adverse findings
            </Typography>
          </Box>
        </Box>

        {/* Status */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 14,
              minWidth: 60,
              color: "#374151",
            }}
          >
            Status:
          </Typography>
          <Box
            sx={{
              backgroundColor: "#f0f9ff",
              border: "1px solid #0ea5e9",
              borderRadius: "16px",
              px: 2,
              py: 0.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 500, fontSize: 12, color: "#0c4a6e" }}
            >
              {statusLabel}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* No Exclusions Found */}
        <Box
          sx={{
            backgroundColor: "#f0fdf4",
            border: "1px solid #16a34a",
            borderRadius: "8px",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#16a34a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path
                  d="M9 1L3.5 6.5L1 4"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#166534" }}
            >
              No Exclusions Found
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, color: "#166534", ml: 3 }}>
            No adverse records were found for this individual.
          </Typography>
        </Box>
      </Box>
    );
  }

  // -------------------------------
  // Case: EXCLUSIONS FOUND
  // -------------------------------
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#FFFFFF",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
      }}
    >
      {/* Name Row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2, mb: 1 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, minWidth: 60, color: "#374151" }}
        >
          Name:
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
          {result.items?.[0]?.names?.[0]?.fullName || "N/A"}
        </Typography>
        <Box
          sx={{
            backgroundColor: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "16px",
            px: 2,
            py: 0.5,
            ml: 1,
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: 12, color: "#92400e" }}>
            {result.total_exclusions} Exclusions Found
          </Typography>
        </Box>
      </Box>

      {/* Result */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2, mb: 1 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, minWidth: 60, color: "#374151" }}
        >
          Result:
        </Typography>
        <Box
          sx={{
            backgroundColor: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "16px",
            px: 2,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
            <path
              d="M7.13397 0.5C7.51887 -0.166667 8.48113 -0.166667 8.86603 0.5L15.7942 12.5C16.1791 13.1667 15.698 14 14.9282 14H1.0718C0.301962 14 -0.179063 13.1667 0.205837 12.5L7.13397 0.5Z"
              fill="#f59e0b"
            />
            <path
              d="M8 5V8M8 10.5H8.005"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Typography sx={{ fontWeight: 500, fontSize: 12, color: "#92400e" }}>
            Adverse findings detected
          </Typography>
        </Box>
      </Box>

      {/* Status */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, minWidth: 60, color: "#374151" }}
        >
          Status:
        </Typography>
        <Typography sx={{ fontWeight: 500, fontSize: 14, color: "#92400e" }}>
          {result.total_exclusions} Exclusions found
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Exclusion Details */}
      <Box
        sx={{
          backgroundColor: "#fef9f5",
          border: "1px solid #f59e0b",
          borderRadius: "8px",
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
            <path
              d="M7.13397 0.5C7.51887 -0.166667 8.48113 -0.166667 8.86603 0.5L15.7942 12.5C16.1791 13.1667 15.698 14 14.9282 14H1.0718C0.301962 14 -0.179063 13.1667 0.205837 12.5L7.13397 0.5Z"
              fill="#f59e0b"
            />
            <path
              d="M8 5V8M8 10.5H8.005"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#92400e" }}>
            Exclusion Detail
          </Typography>
        </Box>
        <MergedExclusionMatchCards
          result={result}
          npi={criteria.npi}
          ssn={criteria.ssn}
        />
      </Box>
    </Box>
  );
};

function exclusionListLabel(exclusion: AdverseCheckExclusion): string {
  const code = String(exclusion.listCode || "").trim().toUpperCase();
  const labels: Record<string, string> = {
    OIG: "OIG / LEIE",
    SAM: "GSA / SAM",
    MOO: "Medicare Opt Out",
    OFAC: "OFAC",
  };
  if (labels[code]) return labels[code];
  if (String(exclusion.EXCLUSION_ORIGIN || "").toUpperCase() === "STATE" && code) {
    return `State - ${code} Medicaid`;
  }
  return code || "Source not reported";
}

function sourceFreshness(exclusion: AdverseCheckExclusion): string {
  const data = exclusion._DATA || {};
  const modified = String(data._modified || "").trim();
  const file = String(data._file || "").trim();
  if (modified && file) return `${modified} (${file})`;
  return modified || file || "Not provided";
}

/**
 * My Facility → Exclusion & Sanction Check entry (#10113):
 * title + subtitle + New check | History tabs wrap the existing check form.
 * History list UI lands in #10121.
 */
export default function OIGAdverseCheckForm({
  onSuccess,
  facilityData,
  oigTabActive = false,
  onWatchGuideClick,
  watchGuideLoading,
  watchGuideIconSrc,
  showWatchGuide,
}: OIGFormProps) {
  const [activeTab, setActiveTab] =
    useState<ExclusionCheckSurfaceTab>("new-check");
  const [historyRefreshCounter, setHistoryRefreshCounter] = useState(0);

  return (
    <ExclusionCheckShell
      activeTab={activeTab}
      onTabChange={setActiveTab}
      newCheckContent={
        <ProviderOIGAdverseCheckForm
          onSuccess={onSuccess}
          facilityData={facilityData}
          oigTabActive={oigTabActive}
          onWatchGuideClick={onWatchGuideClick}
          watchGuideLoading={watchGuideLoading}
          watchGuideIconSrc={watchGuideIconSrc}
          showWatchGuide={showWatchGuide}
          onHistoryPersisted={() =>
            setHistoryRefreshCounter((c) => c + 1)
          }
        />
      }
      historyContent={
        <ExclusionCheckHistoryList
          facilityData={facilityData}
          active={activeTab === "history"}
          refreshKey={historyRefreshCounter}
        />
      }
    />
  );
}
